// One number for whether a plan got better or worse. Hard gates stay out of the average.
//
// Every component can lose points and none is handed a free 1.0: a component that has nothing
// to grade on a plan reports `graded: false` and its weight is renormalised away, so the score
// never rewards a plan for the measure not applying. The result is versioned, and the same plan
// measured against the same contract gives the same number on the tile, in the studio and in a
// loop.
import { countedWeightPounds } from "@CommonUtils/pog/productWeight";
import {
  AESTHETICS_VERSION,
  APPROVAL_BLOCK_FILL,
  blockingAt,
  gradedLevelsOf,
  levelGradedOn,
  resolveAestheticsRules,
  type AestheticsRules,
  type AestheticsWeights,
  type LevelScopes,
  type SavedAestheticsRules,
} from "./aestheticsRules";
import {
  PATH_SEPARATOR,
  effectiveAttrs,
  lengthLabel,
  namesNoBlock,
  type Flag,
  type StudioDraft,
  type StudioItem,
  type StudioRackBoard,
  type StudioRunGeometry,
  walkSplitScopes,
  wholeLength,
} from "./studioTypes";
import {
  orderlinessBeyondChance,
  scoreBlockOrder,
  scoreBlockShape,
  type BrokenBlock,
  type ShapeWall,
} from "./blockShape";
import { bayShelfOf, boardIndexAt, rowWindowsOf } from "./wallRows";

/** Where the hierarchy the plan is graded against came from: the saved tree, the plan's own CDT
 *  columns, or (Studio only) inference from the layout. */
export type AestheticsContract = "finalized" | "columns" | "inferred";

/**
 * Where the bands rules-kept and the band-broken check grade came from.
 *
 *   draft          the Studio draft's own accepted restrictions (skuIds already resolved).
 *   request        the per-shelf `variants_restrictions` the store's generation request sent
 *                  the pogger: what the solve was actually given.
 *   emission       the CDT materializer's emission stored beside that request (its `rows`),
 *                  when the request itself is not to hand.
 *   legacy fences  the `variantRestrictions` stored on the pog's shelves. Read only when none
 *                  of the above exists: a store pog's stored fences can be category-wide groups
 *                  the generation never carried, so a result graded on them says so.
 */
export type BandSource = "draft" | "request" | "emission" | "legacy fences";

/**
 * Where the core range the unplaced check counts came from.
 *
 *   request   the store's generation request: `min_distribution.store_range_items` and
 *             `min_distribution.core_range_items` (their `required_items`), the products the
 *             solve was told it must place.
 *   pog flag  the products the pog itself flags `inCoreRange`, placed or unranged, when the
 *             request is not to hand.
 */
export type CoreRangeSource = "request" | "pog flag";

/** The products a plan must place; any other ranged product may be left off. */
export interface CoreRange {
  source: CoreRangeSource;
  skuIds: readonly string[];
}

/** A band the merchant accepted, in the one shape the score grades, whatever its source. */
export interface BandRule {
  id: string;
  condition: "MUST_HAVE" | "MUST_HAVE_ONLY" | "MUST_NOT_HAVE" | "MAY_HAVE" | "CAN_HAVE";
  /** What the merchant calls the banded value. */
  label: string;
  /** The SKUs the rule governs. Empty with `resolved: false` when the label named something
   *  no column holds: that rule is reported as not graded rather than guessed at. */
  skuIds: readonly string[];
  resolved: boolean;
  /** Cells the rule fences. `run` absent matches every run; `bays` empty matches every bay.
   *  `board`, on a cell several boards share (a rack on its host), is the board a MUST_HAVE asks for. */
  cells: readonly { run?: number; shelf: number; bays: readonly number[]; board?: number }[];
}

export interface AestheticsInput {
  items: readonly StudioItem[];
  runs: readonly StudioRunGeometry[];
  draft: StudioDraft;
  /** Omitted, the detector half stands down and CDT reports the ordering half alone. */
  flags?: readonly Flag[];
  /** House rules; anything omitted is filled positionally from the spine. */
  rules?: Partial<SavedAestheticsRules>;
  /** Omitted, the range component and its gates report `evaluated: false`. */
  rangedSkuIds?: readonly string[];
  /** Attributes of ranged SKUs the plan did not place: a brand dropped whole has no placed item
   *  to name it, so without these the no-brand-eliminated gate cannot see it go. */
  unplacedAttrs?: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  /** Omitted, the rules-kept component is not graded. */
  bands?: readonly BandRule[];
  /** Where `bands` came from; reported back as `bandSource`. */
  bandSource?: BandSource;
  /** Given, the unplaced check counts only these products (an empty set grades nothing).
   *  Omitted, it counts the whole range, as before 3.6.0. */
  coreRange?: CoreRange;
  /** Whether `draft.spine` is the saved contract or was inferred from this plan. */
  contract?: AestheticsContract;
  /** The column below the last graded level whose rank orders a leaf block's products. */
  leafAttr?: string;
}

/** What a plan is graded against beyond its own placements: the bands and the range. */
export type AestheticsScope = Pick<AestheticsInput, "bands" | "rangedSkuIds">;

export interface SubScore {
  /** 0–1. */
  score: number;
  detail: Readonly<Record<string, number>>;
}

/** A component of the 100, with the weight it carried. */
export interface ComponentScore extends SubScore {
  weight: number;
  /** false = nothing on this plan to grade; the weight is renormalised away. */
  graded: boolean;
}

export type ComponentKey = "blocking" | "cdt" | "fullness" | "rulesKept" | "ergonomics" | "range";

/** Every reason the score can give. Closed, so the copy for each one is guaranteed to exist. */
export type AestheticsReasonCode =
  | "blockingPair"
  | "blockingOne"
  | "blockingPlain"
  | "ruleFence"
  | "ruleMissing"
  | "rulePlain"
  | "gap"
  | "fullnessPlain"
  | "brandDropped"
  | "unplaced"
  | "sizeOrder"
  | "orderPlain"
  | "cdtPlain"
  | "heavyHigh"
  | "bigOverSmall";

/** Every gate the score can fail. Closed for the same reason. */
export type AestheticsGateId =
  | "overlap"
  | "past-edge"
  | "empty-shelf"
  | "unplaced"
  | "brand-eliminated"
  | "block-in-pieces"
  | "block-staircased"
  | "block-out-of-shape"
  | "band-broken"
  | "shelves-not-full";

/**
 * Why points were lost, as data. The words live in the translations, so the same reason reads
 * in the merchant's own language and no engineering term reaches the screen.
 */
export interface AestheticsReason {
  code: AestheticsReasonCode;
  /** Points lost out of 100, rounded to one decimal. */
  points: number;
  values: Readonly<Record<string, string | number>>;
}

export interface GateResult {
  id: AestheticsGateId;
  label: string;
  /** false when the gate could not be evaluated (see `evaluated`). */
  passed: boolean;
  evaluated: boolean;
  count: number;
  examples: readonly string[];
}

export interface WorstBlock {
  /** Block path, e.g. "Detergent › Tide › Liquid". */
  block: string;
  /** (max start − min start) + (max end − min end), in raw planogram units. */
  raggedCm: number;
  /** The block's widest pack: the unit raggedness is read against. */
  widestPackCm: number;
  shelves: readonly number[];
  footprintCm: number;
  score: number;
}

export interface EmptiestShelf {
  run: number;
  shelf: number;
  largestGapCm: number;
  gapAtXCm: number;
  totalAirCm: number;
}

/** The shelf whose products leave the most height unused under the board above. */
export interface AiriestShelf {
  run: number;
  shelf: number;
  /** Air beyond one more pack, as a share of the opening, width-weighted along the row. */
  airShare: number;
}

export interface OrderingBreak {
  /** Which ordering rule broke: size / tier / last-in-block / flavour ribbon. */
  rule: string;
  block: string;
  detail: string;
  violations: number;
}

/** A band the plan broke, named so a person can go and look at it. */
export interface RuleBreak {
  band: string;
  condition: BandRule["condition"];
  productName: string;
  shelf: number;
  facings: number;
}

export interface AestheticsScore {
  /** 0–100, rounded. */
  score: number;
  /** The total `score` rounds from, so two plans the tile shows alike can still be told apart. */
  exactScore: number;
  /** Bumped whenever a weight or a measure changes. */
  version: string;
  contract: AestheticsContract;
  /** Where the graded bands came from; null when the plan was given none. A result graded on
   *  `"legacy fences"` was graded on the pog's stored fences, not on what the solve was sent. */
  bandSource: BandSource | null;
  /** Where the core range the unplaced check counted came from; null when it counted the whole
   *  range. */
  coreRangeSource: CoreRangeSource | null;
  components: Readonly<Record<ComponentKey, ComponentScore>>;
  /** Biggest reasons first. */
  penalties: readonly AestheticsReason[];
  weights: AestheticsWeights;
  blocking: SubScore & {
    shape: SubScore;
    vertical: SubScore;
    /** Each brand standing the height of its section (graded with the vertical stripes). */
    columns: SubScore;
    horizontal: SubScore;
    contiguity: SubScore;
  };
  cdt: SubScore & { detector: SubScore | null; ordering: SubScore };
  fullness: SubScore;
  ergonomics: SubScore;
  rulesKept: SubScore;
  range: SubScore;
  gates: readonly GateResult[];
  /** True only when every gate that could be evaluated passed. */
  gatesPassed: boolean;
  worst: {
    /** Blocks in pieces or out of shape, worst first. */
    brokenBlocks: readonly BrokenBlock[];
    blocks: readonly WorstBlock[];
    /** Brands that leave part of their section's height to other brands, worst first. */
    columns: readonly ColumnBreak[];
    emptiestShelf: EmptiestShelf | null;
    airiestShelf: AiriestShelf | null;
    ordering: OrderingBreak | null;
    rules: readonly RuleBreak[];
  };
  /** Every entry `worst` keeps the first three (or one) of, worst first: the review player lists
   *  them all, so that no problem hides behind the worst. */
  flagged: {
    brokenBlocks: readonly BrokenBlock[];
    blocks: readonly WorstBlock[];
    ordering: readonly OrderingBreak[];
    rules: readonly RuleBreak[];
  };
  rules: AestheticsRules;
}

interface Stretch {
  item: StudioItem;
  attrs: Record<string, string | null>;
  run: number;
  shelf: number;
  bay: number;
  x: number;
  width: number;
  hFacings: number;
  packWidth: number;
  rackBoard?: StudioRackBoard;
  board?: number;
  packHeight: number;
  stackHeight: number | undefined;
}

/* A fixed length is a different distance in each raw unit, so every length the score compares
   against is a share of the plan's own bay, calibrated on a 48 in bay of 4800 raw units. */

/** Slack under which an overlap or a gap does not count: a tenth of an inch on a 48 in bay. */
const EPS_BAY_SHARE = 10 / 4800;

/** Stands in for a pack with no width so raggedness still divides: a centimetre on a 48 in bay. */
const MIN_PACK_BAY_SHARE = 39 / 4800;

/** Only for a plan with no bays at all, where nothing is measured against it. */
const NO_BAYS_WIDTH = 4800;

/** The plan's own bay: the first run's stated bay width, else a run's mean bay. */
export const planBayWidthOf = (runs: readonly StudioRunGeometry[]): number => {
  const stated = runs.find((run) => run.bayWidthCm > 0);
  if (stated) return stated.bayWidthCm;
  const measured = runs.find((run) => run.widthCm > 0 && run.bays > 0);
  return measured ? measured.widthCm / measured.bays : NO_BAYS_WIDTH;
};

const epsOf = (runs: readonly StudioRunGeometry[]): number =>
  planBayWidthOf(runs) * EPS_BAY_SHARE;

const clamp01 = (value: number) => (value < 0 ? 0 : value > 1 ? 1 : value);

const laneKey = (run: number, shelf: number) => `${run}|${shelf}`;

const round3 = (value: number) => +value.toFixed(3);

/** The pack laid at a placement: a tray on one shelf may stand as side-on units on another, and
 *  a quarter of the tray is no measure of the units. Shared with the Studio's plan verdict. */
export const packWidthOf = (
  item: Pick<StudioItem, "facingWidthCm">,
  placement: Pick<StudioItem["placements"][number], "widthCm" | "hFacings">,
): number =>
  placement.hFacings > 0 || !(item.facingWidthCm > 0)
    ? placement.widthCm / Math.max(1, placement.hFacings)
    : item.facingWidthCm;

/** The rows `scoreBlockShape` reads off each run, with their live windows; shared with the
 *  Studio's plan verdict so both count the same blocks in pieces. */
export const shapeWallsOf = (runs: readonly StudioRunGeometry[]): ShapeWall[] =>
  runs.map((run) => ({
    run: run.run,
    widthCm: run.widthCm,
    rows: run.shelves.map((shelf) => ({
      rank: shelf.rank,
      spans: shelf.spans
        ? shelf.spans.map((span) => ({ from: span.xCm, to: span.xCm + span.widthCm }))
        : [{ from: 0, to: run.widthCm }],
      live: rowWindowsOf(run)
        .filter((window) => window.rank === shelf.rank)
        .map((window) => ({ from: window.startCm, to: window.endCm })),
    })),
  }));

const toStretches = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
): Stretch[] => {
  const bayWidthOf = new Map(runs.map((run) => [run.run, run.bayWidthCm]));
  const out: Stretch[] = [];
  for (const item of items) {
    const attrs = effectiveAttrs(item, draft);
    for (const placement of item.placements) {
      if (!(placement.widthCm > 0)) continue;
      const bayWidth = bayWidthOf.get(placement.run) || 0;
      out.push({
        item,
        attrs,
        run: placement.run,
        shelf: placement.shelf,
        bay: placement.bay ?? (bayWidth > 0 ? Math.floor(placement.xCm / bayWidth) + 1 : 1),
        x: placement.xCm,
        width: placement.widthCm,
        hFacings: placement.hFacings,
        ...(placement.rackBoard && { rackBoard: placement.rackBoard }),
        ...(placement.board !== undefined && { board: placement.board }),
        packWidth: packWidthOf(item, placement),
        packHeight: item.heightCm,
        stackHeight: placement.stackHeightCm,
      });
    }
  }
  /* Deterministic: the same plan must give the same number however the items arrived. */
  return out.sort(
    (a, b) =>
      a.run - b.run ||
      a.shelf - b.shelf ||
      a.x - b.x ||
      (a.item.skuId < b.item.skuId ? -1 : a.item.skuId > b.item.skuId ? 1 : 0),
  );
};

type Span = { from: number; to: number };

const without = (spans: readonly Span[], holes: readonly Span[]): Span[] =>
  holes.reduce<Span[]>(
    (kept, hole) =>
      kept.flatMap((span) =>
        [
          { from: span.from, to: Math.min(span.to, hole.from) },
          { from: Math.max(span.from, hole.to), to: span.to },
        ].filter((part) => part.to > part.from),
      ),
    [...spans],
  );

const merged = (spans: readonly Span[]): Span[] =>
  [...spans]
    .sort((a, b) => a.from - b.from)
    .reduce<Span[]>((out, span) => {
      const last = out[out.length - 1];
      if (last && span.from <= last.to) last.to = Math.max(last.to, span.to);
      else out.push({ ...span });
      return out;
    }, []);

/** DEV-3717: shape and order read each rack board at its own height over the rack's span, in the
 *  wall row it lines up with or a row of its own; every other measure grades it on its host's row. */
const onRackBoards = (
  stretches: readonly Stretch[],
  walls: readonly ShapeWall[],
  runs: readonly StudioRunGeometry[],
): { stretches: Stretch[]; walls: ShapeWall[] } => {
  const tierOf = (stretch: Stretch, board: StudioRackBoard) => {
    const rack = `${laneKey(stretch.run, stretch.shelf)}|${board.fromCm}|${board.toCm}`;
    return { rack, key: `${rack}|${board.level}` };
  };
  const tiers = new Map<string, { run: number; host: number; rack: string; y: number; span: Span }>();
  for (const stretch of stretches) {
    const board = stretch.rackBoard;
    if (!board) continue;
    const { rack, key } = tierOf(stretch, board);
    if (tiers.has(key)) continue;
    const span = { from: board.fromCm, to: board.toCm };
    tiers.set(key, { run: stretch.run, host: stretch.shelf, rack, y: board.rowYCm, span });
  }
  if (tiers.size === 0) return { stretches: [...stretches], walls: [...walls] };

  const shelfOf = new Map(
    runs.flatMap((run) => run.shelves.map((shelf) => [laneKey(run.run, shelf.rank), shelf] as const)),
  );
  type Row = { y: number; keys: string[]; racks: Set<string>; spans: Span[]; live?: Span[] };
  const rankOf = new Map<string, number>();
  const boardWalls = walls.map((wall) => {
    const own = [...tiers].filter(([, tier]) => tier.run === wall.run);
    const wallRows = [...wall.rows]
      .sort((a, b) => a.rank - b.rank)
      .map((row) => {
        const key = laneKey(wall.run, row.rank);
        const hosted = own.filter(([, tier]) => tier.host === row.rank).map(([, tier]) => tier.span);
        const shelf = shelfOf.get(key);
        return {
          rank: row.rank,
          boards: !shelf?.pegboard,
          y: shelf?.yCm ?? Number.NaN,
          keys: [key],
          racks: new Set<string>(),
          spans: without(row.spans, hosted),
          ...(row.live && { live: without(row.live, hosted) }),
        };
      });
    const boardRows: Row[] = [];
    for (const [key, tier] of own) {
      const fits = (row: Row) => row.y === tier.y && !row.racks.has(tier.rack);
      let row: Row | undefined =
        wallRows.find((wall) => wall.boards && wall.rank !== tier.host && fits(wall)) ??
        boardRows.find(fits);
      if (!row) {
        row = { y: tier.y, keys: [], racks: new Set(), spans: [], live: [] };
        boardRows.push(row);
      }
      row.keys.push(key);
      row.racks.add(tier.rack);
      row.spans = merged([...row.spans, tier.span]);
      if (row.live) row.live = merged([...row.live, tier.span]);
    }
    // A board's own row stands over the first wall row at or below its height.
    const walled = new Set<Row>(wallRows);
    const rows: Row[] = [...wallRows];
    for (const row of [...boardRows].sort((a, b) => b.y - a.y)) {
      const at = rows.findIndex((other) => (walled.has(other) ? other.y <= row.y : other.y < row.y));
      rows.splice(at < 0 ? rows.length : at, 0, row);
    }
    rows.forEach((row, index) => row.keys.forEach((key) => rankOf.set(key, index + 1)));
    return {
      ...wall,
      rows: rows.map((row, index) => ({
        rank: index + 1,
        spans: row.spans,
        ...(row.live && { live: row.live }),
      })),
    };
  });

  return {
    walls: boardWalls,
    stretches: stretches.map((stretch) => {
      const board = stretch.rackBoard;
      if (!board) {
        return { ...stretch, shelf: rankOf.get(laneKey(stretch.run, stretch.shelf)) ?? stretch.shelf };
      }
      return {
        ...stretch,
        shelf: rankOf.get(tierOf(stretch, board).key) ?? stretch.shelf,
        x: board.xCm,
        width: board.widthCm,
        // The physical pitch: the placement's own width is scaled to its slice of the host row.
        packWidth: packWidthOf(stretch.item, { widthCm: board.widthCm, hFacings: stretch.hFacings }),
      };
    }),
  };
};

const groupBy = <T>(list: readonly T[], key: (entry: T) => string): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const entry of list) {
    const k = key(entry);
    const bucket = map.get(k);
    if (bucket) bucket.push(entry);
    else map.set(k, [entry]);
  }
  return map;
};

/** Lanes in reading order: left to right within a shelf. */
const lanesOf = (stretches: readonly Stretch[]): Map<string, Stretch[]> => {
  const lanes = groupBy(stretches, (s) => laneKey(s.run, s.shelf));
  lanes.forEach((list) => list.sort((a, b) => a.x - b.x));
  return lanes;
};

/** Magnitude in a comparable unit plus its family; only same-family values compare. */
const sizeMagnitude = (label: string | null): { family: string; magnitude: number } | null => {
  if (!label) return null;
  const number = parseFloat(label.match(/-?\d+(?:\.\d+)?/)?.[0] ?? "");
  if (!Number.isFinite(number)) return null;
  if (/fl\s*oz/i.test(label)) return { family: "volume", magnitude: number };
  if (/\bml\b/i.test(label)) return { family: "volume", magnitude: number * 0.033814 };
  if (/\bl\b|liter|litre/i.test(label)) return { family: "volume", magnitude: number * 33.814 };
  if (/\blb\b|pound/i.test(label)) return { family: "mass", magnitude: number * 16 };
  if (/\boz\b|ounce/i.test(label)) return { family: "mass", magnitude: number };
  if (/\bct\b|count|\bpk\b|pack|pac|pod/i.test(label))
    return { family: "count", magnitude: number };
  if (/load/i.test(label)) return { family: "loads", magnitude: number };
  return { family: "bare", magnitude: number };
};

/** The path of values identifying the block a stretch sits in, down to `depth` levels. */
const pathKey = (stretch: Stretch, attrs: readonly string[]) =>
  attrs.map((attr) => stretch.attrs[attr] ?? "—").join(PATH_SEPARATOR);

/** The last segment of a block path: what a person would call the block. */
const lastSegment = (key: string) => key.split(PATH_SEPARATOR).pop() ?? key;

/** Opens with the count or measure `sizeMagnitude` reads: "12 Mega", "64 oz", never
 *  "Snap & Go 4 pack", whose words name a sub-brand. */
const opensWithSize = (label: string): boolean => {
  const text = label.trim();
  return /^\d/.test(text) && sizeMagnitude(text) !== null;
};

/** The level whose blocks are sizes: the designated size level when it is graded, else the
 *  deepest graded level below the brand's (the top level stands for an ungraded brand). */
export const sizeTierAttrOf = (rules: AestheticsRules): string | null => {
  const levels = gradedLevelsOf(rules).map((level) => level.attr);
  if (levels.includes(rules.sizeAttr)) return rules.sizeAttr;
  const brand = Math.max(0, levels.indexOf(rules.brandAttr));
  return levels.length - 1 > brand ? levels[levels.length - 1] : null;
};

/** A size tier value naming no sub-brand: a composite whose sub-brand part is empty and which
 *  carries a size (": Small : Powder : 3 : Regular"), or a value opening with one ("6 Double").
 *  A blank value is bad data, never a size. */
export const namesNoSubBrand = (value: string): boolean => {
  if (namesNoBlock(value)) return false;
  const [subBrand, ...rest] = value.split(":");
  return rest.length
    ? subBrand.trim() === "" && rest.some(opensWithSize)
    : opensWithSize(subBrand);
};

/** A block of the size tier naming no sub-brand: its pieces are no defect a gate or card stops on. */
export const isSizeOnlyTier = (
  rules: AestheticsRules,
  block: { block: string; depth: number },
): boolean => {
  const tier = sizeTierAttrOf(rules);
  return (
    tier !== null &&
    gradedLevelsOf(rules)[block.depth]?.attr === tier &&
    namesNoSubBrand(lastSegment(block.block))
  );
};

/** A block a person would know by its name: one a placeholder names is graded, never named. */
const isNamed = (block: { block: string }) => !namesNoBlock(lastSegment(block.block));

/* ---------------------------------------------------------------- vertical stripes */

/** An edge off by `offCm`: `jitterPacks` packs forgiven, nothing left `raggedZeroPacks` packs
 *  past that. */
const edgeScore = (offCm: number, widest: number, rules: AestheticsRules): number =>
  clamp01(1 - Math.max(0, offCm - rules.jitterPacks * widest) / (rules.raggedZeroPacks * widest));

/** Raggedness vs widest pack: one pack per edge is forgiven (blocks end on pack boundaries). */
const scoreVerticalBlocking = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
  scopes: LevelScopes,
  bayWidthCm: number,
): {
  sub: SubScore;
  worst: WorstBlock[];
  ragged: WorstBlock[];
  /** Every stripe graded, with the depth of its level. */
  stripes: (WorstBlock & { depth: number })[];
  graded: boolean;
} => {
  const blocks: (WorstBlock & { depth: number })[] = [];
  let weighted = 0;
  let weight = 0;
  let raggedTotal = 0;
  const order = gradedLevelsOf(rules);
  const levels = order.filter((level) => levelGradedOn(rules, level, "vertical", scopes));

  for (const level of levels) {
    /* A stripe is keyed by its FULL path, bands included: "Downy › Sheets" and "Downy › Beads"
       are two stripes, not one block spanning the wall. On an all-vertical hierarchy the full
       path and the vertical path are the same. */
    const path = order.slice(0, order.indexOf(level) + 1).map((entry) => entry.attr);
    const levelWeight = level.weight;
    const grouped = groupBy(stretches, (s) => pathKey(s, path));
    grouped.forEach((members, key) => {
      // A scope the merchant banded is graded by the band measure, not on its x edges.
      if (blockingAt(rules, level, scopes, members[0].item.skuId) !== "vertical") {
        return;
      }
      const byLane = groupBy(members, (s) => laneKey(s.run, s.shelf));
      // A block confined to one shelf has no vertical edges to be straight.
      if (byLane.size < 2) return;
      // Per run: a block legitimately restarts its x in the next aisle.
      const byRun = groupBy([...byLane.entries()], ([lane]) => lane.split("|")[0]);
      const widest =
        members.reduce((max, s) => Math.max(max, s.packWidth), 0) ||
        bayWidthCm * MIN_PACK_BAY_SHARE;
      const isBayOwner = rules.bayOwners.some((owner) =>
        key.toLowerCase().includes(owner.toLowerCase()),
      );
      byRun.forEach((laneEntries) => {
        if (laneEntries.length < 2) return;
        const starts: number[] = [];
        const ends: number[] = [];
        const shelves: number[] = [];
        let footprint = 0;
        for (const [lane, list] of laneEntries) {
          starts.push(Math.min(...list.map((s) => s.x)));
          ends.push(Math.max(...list.map((s) => s.x + s.width)));
          shelves.push(Number(lane.split("|")[1]));
          footprint += list.reduce((sum, s) => sum + s.width, 0);
        }
        let ragged =
          Math.max(...starts) - Math.min(...starts) + (Math.max(...ends) - Math.min(...ends));
        // A bay owner also owes the distance from its edges to the bay line.
        if (isBayOwner && bayWidthCm > 0) {
          const offBayLine = (edge: number) => {
            const offset = ((edge % bayWidthCm) + bayWidthCm) % bayWidthCm;
            return Math.min(offset, bayWidthCm - offset);
          };
          const median = (values: number[]) =>
            [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];
          ragged += offBayLine(median(starts)) + offBayLine(median(ends));
        }
        const score = edgeScore(ragged, widest, rules);
        blocks.push({
          block: key,
          raggedCm: +ragged.toFixed(1),
          widestPackCm: +widest.toFixed(1),
          shelves: shelves.sort((a, b) => a - b),
          footprintCm: +footprint.toFixed(1),
          score: round3(score),
          depth: level.depth,
        });
        weighted += score * footprint * levelWeight;
        weight += footprint * levelWeight;
        raggedTotal += ragged * footprint * levelWeight;
      });
    });
  }

  const ragged = blocks
    .filter((block) => block.score < 1)
    .map(({ depth: _depth, ...block }) => block)
    .sort(
      (a, b) =>
        (1 - b.score) * b.footprintCm - (1 - a.score) * a.footprintCm ||
        (a.block < b.block ? -1 : a.block > b.block ? 1 : 0),
    );

  return {
    graded: weight > 0,
    sub: {
      score: weight > 0 ? weighted / weight : 1,
      detail: {
        blocks: blocks.length,
        meanRaggedCm: weight > 0 ? +(raggedTotal / weight).toFixed(1) : 0,
        footprintCm: +weight.toFixed(0),
      },
    },
    worst: ragged.slice(0, 3),
    ragged,
    stripes: blocks,
  };
};

/* ---------------------------------------------------------------- staircases */

export interface StaircasedBlock {
  /** Block path of a top-level stripe. */
  block: string;
  /** How much of its bounding rectangle the block fills (block shape's `fill`). */
  fill: number;
  /** Its left and right edges' raggedness, in its widest pack (the edge measure's own unit). */
  raggedPacks: number;
}

/**
 * Top-level stripes whose shelves step sideways. A staircase is one connected piece, since each
 * shelf overlaps the next, so it never fails block-in-pieces, and its cost in shape and edges is
 * averaged over every block on the wall. Read on block shape's own fill: a block filling less
 * than `staircaseFill` of its rectangle is not the column the merchant asked for. Its ragged
 * edges are named with it but do not decide: they read x alone, so a stripe on a wall whose
 * bays carry different shelves looks ragged while filling every board it has. A block in pieces
 * is block-in-pieces' to report, not this gate's. Bands are not read: a brand banded across a horizontal wall runs on
 * from one shelf to the next like a line of text, so its rectangle is never full by design.
 */
const staircasedBlocks = (
  broken: readonly BrokenBlock[],
  stripes: readonly (WorstBlock & { depth: number })[],
  rules: AestheticsRules,
): StaircasedBlock[] => {
  const fillOf = new Map<string, number>();
  const split = new Set<string>();
  for (const block of broken) {
    if (block.depth !== 0) continue;
    fillOf.set(block.block, Math.min(fillOf.get(block.block) ?? 1, block.fill));
    if (block.pieces > 1 || block.extraRuns > 0) split.add(block.block);
  }
  const worst = new Map<string, StaircasedBlock>();
  for (const stripe of stripes) {
    if (stripe.depth !== 0 || split.has(stripe.block) || namesNoBlock(lastSegment(stripe.block))) {
      continue;
    }
    const raggedPacks = +(stripe.raggedCm / stripe.widestPackCm).toFixed(2);
    const fill = fillOf.get(stripe.block) ?? 1;
    if (fill >= rules.staircaseFill) continue;
    const seen = worst.get(stripe.block);
    worst.set(stripe.block, {
      block: stripe.block,
      fill,
      raggedPacks: Math.max(seen?.raggedPacks ?? 0, raggedPacks),
    });
  }
  return [...worst.values()].sort((a, b) => a.fill - b.fill || b.raggedPacks - a.raggedPacks);
};

/* ---------------------------------------------------------------- brand columns */

export interface ColumnBreak {
  /** Section › brand. */
  block: string;
  /** The shelves the brand's column stands on. */
  shelves: readonly number[];
  /** The section's shelves under its column that it leaves to other brands. */
  missing: readonly number[];
  displacedCm: number;
}

/**
 * A stripe runs the height of its section. Where a section spans several shelves, each brand
 * stands as one column across every shelf of the section under it: Downy sheets on both sheet
 * shelves, over Downy beads on both bead shelves. A brand that holds only some of those shelves
 * hands the rest of its column to other brands, and the section reads as brands snaking shelf by
 * shelf. Block shape cannot see that (each brand is still one tidy rectangle) and the ragged
 * edges skip a block that stands on one shelf, so it is graded here.
 *
 * The section is the block above the brand: the graded levels above the first one whose every
 * block holds a single brand. A brand's column is its largest piece (its runs along each shelf,
 * joined where they overlap in x on another shelf). Each shelf of the section that the column's
 * x-range crosses and the column misses displaces the column's width less `jitterPacks` packs.
 * As in block shape, a break costs `blockPieceCost` times the square root of the share of the
 * section it displaces, a section scores one minus its damage, and the measure is the mean over
 * sections, floored at 0.
 *
 * A section is graded only when it stands at least one brand as a column: one that lays every
 * brand along a single shelf is banded, and block shape grades it as it is.
 */
const scoreBrandColumns = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
  scopes: LevelScopes,
  skipUnvalued: boolean,
  eps: number,
): { sub: SubScore; worst: ColumnBreak[]; graded: boolean } => {
  const none = { graded: false, sub: { score: 1, detail: { sections: 0 } }, worst: [] };
  const levels = gradedLevelsOf(rules);
  const order = levels.map((level) => level.attr);
  // "Others" or "Unspecified" is a bucket, not a brand: it stands no column and breaks none.
  const brandOf = (stretch: Stretch) => {
    const brand = stretch.attrs[rules.brandAttr]?.trim();
    return brand && !namesNoBlock(brand) ? brand : null;
  };
  if (!rules.brandAttr) return none;

  const brandDepth = order.findIndex((_, depth) => {
    const path = order.slice(0, depth + 1);
    const brandAt = new Map<string, string>();
    return stretches.every((stretch) => {
      const brand = brandOf(stretch);
      if (brand === null) return true;
      const key = pathKey(stretch, path);
      const seen = brandAt.get(key);
      if (seen === undefined) brandAt.set(key, brand);
      return seen === undefined || seen === brand;
    });
  });
  if (brandDepth <= 0 || !levelGradedOn(rules, levels[brandDepth], "vertical", scopes)) {
    return none;
  }
  const sectionPath = order.slice(0, brandDepth);
  const sectionOf = (stretch: Stretch) => `${stretch.run}␟${pathKey(stretch, sectionPath)}`;

  /* A brand's runs along each shelf: neighbours in reading order, nothing foreign between. */
  const runsOf = new Map<string, { shelf: number; from: number; to: number; mass: number }[]>();
  lanesOf(stretches).forEach((lane) => {
    let previous = "";
    for (const stretch of lane) {
      const brand = brandOf(stretch);
      const id = brand === null ? "" : `${sectionOf(stretch)}␟${brand}`;
      const runs = runsOf.get(id) ?? [];
      const last = runs[runs.length - 1];
      if (id && id === previous && last) {
        last.to = Math.max(last.to, stretch.x + stretch.width);
        last.mass += stretch.width;
      } else if (id) {
        runs.push({
          shelf: stretch.shelf,
          from: stretch.x,
          to: stretch.x + stretch.width,
          mass: stretch.width,
        });
        runsOf.set(id, runs);
      }
      previous = id;
    }
  });

  const breaks: ColumnBreak[] = [];
  let damage = 0;
  let sections = 0;
  groupBy(stretches, sectionOf).forEach((members) => {
    if (skipUnvalued && sectionPath.some((attr) => members[0].attrs[attr] == null)) return;
    // A scope the merchant banded stands its brands as bands, not columns.
    if (blockingAt(rules, levels[brandDepth], scopes, members[0].item.skuId) !== "vertical") {
      return;
    }
    const shelves = new Set(members.map((member) => member.shelf));
    if (shelves.size < 2) return;
    const mass = members.reduce((sum, member) => sum + member.width, 0);
    const section = pathKey(members[0], sectionPath);
    const sectionShelves = [...shelves].sort((a, b) => a - b);
    const contiguous = (on: Set<number>) => {
      const rows = sectionShelves.flatMap((shelf, index) => (on.has(shelf) ? [index] : []));
      return rows.length > 0 && rows[rows.length - 1] - rows[0] === rows.length - 1;
    };
    type Piece = {
      mass: number;
      from: number;
      to: number;
      shelves: Set<number>;
      runs: { shelf: number; from: number; to: number }[];
    };
    let standsColumn = false;
    const here: { brand: ColumnBreak; share: number }[] = [];
    const brandColumns: { brand: string; column: Piece; widest: number }[] = [];
    groupBy(
      members.filter((member) => brandOf(member) !== null),
      (member) => brandOf(member) as string,
    ).forEach((own, brand) => {
      const runs = runsOf.get(`${sectionOf(own[0])}␟${brand}`) ?? [];
      const parent = runs.map((_, index) => index);
      const rootOf = (index: number): number =>
        parent[index] === index ? index : (parent[index] = rootOf(parent[index]));
      for (let a = 0; a < runs.length; a++) {
        for (let b = a + 1; b < runs.length; b++) {
          const overlap = Math.min(runs[a].to, runs[b].to) - Math.max(runs[a].from, runs[b].from);
          if (overlap > eps) parent[rootOf(a)] = rootOf(b);
        }
      }
      const pieces = new Map<number, Piece>();
      runs.forEach((run, index) => {
        const root = rootOf(index);
        const piece = pieces.get(root) ?? {
          mass: 0,
          from: Infinity,
          to: -Infinity,
          shelves: new Set<number>(),
          runs: [],
        };
        piece.mass += run.mass;
        piece.from = Math.min(piece.from, run.from);
        piece.to = Math.max(piece.to, run.to);
        piece.shelves.add(run.shelf);
        piece.runs.push(run);
        pieces.set(root, piece);
      });
      const column = [...pieces.values()].reduce((best, piece) =>
        piece.mass > best.mass ? piece : best,
      );
      if (column.shelves.size >= 2) standsColumn = true;
      brandColumns.push({
        brand,
        column,
        widest: own.reduce((max, member) => Math.max(max, member.packWidth), 0),
      });
    });
    /* A short column with other short columns stacked under or over it holds the shelves they
       fill under its width: a stack of blocks, not a snake. A block that also stands on its
       shelves is a neighbour, never part of its stack. */
    const stackedWith = (entry: (typeof brandColumns)[number]) =>
      brandColumns.filter(
        (other) =>
          other !== entry &&
          contiguous(other.column.shelves) &&
          ![...other.column.shelves].some((shelf) => entry.column.shelves.has(shelf)),
      );
    brandColumns.forEach((entry) => {
      const { brand, column, widest } = entry;
      const stack = contiguous(column.shelves) ? stackedWith(entry) : [];
      const width = column.to - column.from;
      const under = new Set(
        members
          .filter(
            (member) =>
              Math.min(column.to, member.x + member.width) - Math.max(column.from, member.x) > eps,
          )
          .map((member) => member.shelf),
      );
      const missing = [...under]
        .filter((shelf) => !column.shelves.has(shelf))
        .sort((a, b) => a - b);
      type Span = { shelf: number; from: number; to: number };
      /** The runs' pieces on a shelf inside [from, to], merged where they touch. */
      const spansOf = (runs: readonly Span[], shelf: number, from = column.from, to = column.to) => {
        const merged: [number, number][] = [];
        runs
          .filter((run) => run.shelf === shelf)
          .map((run): [number, number] => [Math.max(run.from, from), Math.min(run.to, to)])
          .filter(([a, b]) => b > a)
          .sort((a, b) => a[0] - b[0])
          .forEach(([a, b]) => {
            const last = merged[merged.length - 1];
            if (last && a <= last[1] + eps) last[1] = Math.max(last[1], b);
            else merged.push([a, b]);
          });
        return merged;
      };
      const coveredBy = (runs: readonly Span[], shelf: number, from?: number, to?: number) =>
        spansOf(runs, shelf, from, to).reduce((sum, [a, b]) => sum + b - a, 0);
      const allRuns = stack.flatMap((other) => other.column.runs);
      const stackBrands = new Set(stack.map((other) => other.brand));
      const spanOf = (member: Stretch) => ({
        shelf: member.shelf,
        from: member.x,
        to: member.x + member.width,
      });
      const stacked = members.filter((m) => stackBrands.has(brandOf(m) ?? "")).map(spanOf);
      const foreign = members.filter((m) => !stackBrands.has(brandOf(m) ?? "")).map(spanOf);
      /* Another block's products on a shelf it leaves either reach in from its outside edge, with
         only empty shelf between (a ragged edge), or sit inside the stack (a hole). Empty shelf
         is no hole: fullness charges it. */
      const intrusion = (shelf: number) => {
        let edge = 0;
        let inside = 0;
        for (const [a, b] of spansOf(foreign, shelf)) {
          const fromEdge =
            coveredBy(stacked, shelf, column.from, a) < eps ||
            coveredBy(stacked, shelf, b, column.to) < eps;
          if (fromEdge) edge += b - a;
          else inside += b - a;
        }
        return { edge, inside, total: edge + inside };
      };
      const jitter = rules.jitterPacks * widest + eps;
      // A shelf it leaves with no stacked block under it is handed to its neighbours: a snake.
      const filled = missing.every(
        (shelf) =>
          coveredBy(allRuns, shelf) > eps &&
          (rules.stackSeamMayShift
            ? intrusion(shelf).inside <= jitter
            : intrusion(shelf).total <= jitter),
      );
      const edgeIn = Math.max(0, ...missing.map((shelf) => intrusion(shelf).edge));
      /* The seam under it sits as far from its edge as the stack's width no one stacked block
         fills, graded as a vertical edge off by that much. */
      const seamShift = Math.max(
        0,
        ...missing.map(
          (shelf) =>
            coveredBy(allRuns, shelf) -
            Math.max(0, ...stack.map((other) => coveredBy(other.column.runs, shelf))),
        ),
      );
      // As a vertical edge's raggedness adds its two edges, the reach and the shift add up.
      const kept = !filled
        ? 0
        : rules.stackSeamMayShift
          ? edgeScore(edgeIn + seamShift, widest, rules)
          : seamShift <= jitter
            ? 1
            : 0;
      const displaced =
        (1 - kept) *
        missing.reduce((sum) => sum + Math.max(0, width - rules.jitterPacks * widest), 0);
      if (displaced > eps) {
        here.push({
          brand: {
            block: `${section}${PATH_SEPARATOR}${brand}`,
            shelves: [...column.shelves].sort((a, b) => a - b),
            missing,
            displacedCm: +displaced.toFixed(1),
          },
          share: displaced / mass,
        });
      }
    });
    if (!standsColumn) return;
    sections += 1;
    for (const entry of here) {
      damage += rules.blockPieceCost * Math.sqrt(entry.share);
      breaks.push(entry.brand);
    }
  });

  const worst = breaks
    .sort(
      (a, b) =>
        b.displacedCm - a.displacedCm || (a.block < b.block ? -1 : a.block > b.block ? 1 : 0),
    )
    .slice(0, 3);
  return {
    graded: sections > 0,
    sub: {
      score: sections > 0 ? Math.max(0, 1 - damage / sections) : 1,
      detail: { sections, broken: breaks.length, damage: +damage.toFixed(3) },
    },
    worst,
  };
};

/* ---------------------------------------------------------------- horizontal bands */

interface BandBreak {
  block: string;
  shelves: readonly number[];
  score: number;
  footprintCm: number;
}

/**
 * A banded value owns a stretch of shelves, so it is graded on its TOP and BOTTOM edges:
 *   · the shelves it sits on are next to each other (one band, not two), and
 *   · every bay puts the band on the same shelves (a straight edge across the wall).
 * Grading such a value on its left and right edges instead is what made a correctly banded
 * plan score as a broken vertical block.
 */
const scoreBands = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
  scopes: LevelScopes,
  bayRow: (stretch: Stretch) => number,
): { sub: SubScore; worst: BandBreak[]; graded: boolean } => {
  const levels = gradedLevelsOf(rules);
  const banded = levels.filter((level) => levelGradedOn(rules, level, "horizontal", scopes));
  const ladder = scoreTierLadder(stretches, rules);
  if (banded.length === 0) {
    return ladder
      ? { graded: true, sub: ladder, worst: [] }
      : { graded: false, sub: { score: 1, detail: { bands: 0 } }, worst: [] };
  }
  const order = levels.map((level) => level.attr);
  const breaks: BandBreak[] = [];
  let weighted = 0;
  let weight = 0;
  let straightTotal = 0;

  for (const level of banded) {
    const path = order.slice(0, order.indexOf(level.attr) + 1);
    const grouped = groupBy(stretches, (s) => pathKey(s, path));
    grouped.forEach((members, key) => {
      if (blockingAt(rules, level, scopes, members[0].item.skuId) !== "horizontal") return;
      const byRun = groupBy(members, (s) => String(s.run));
      byRun.forEach((runMembers) => {
        const shelves = [...new Set(runMembers.map((s) => s.shelf))].sort((a, b) => a - b);
        const footprint = runMembers.reduce((sum, s) => sum + s.width, 0);
        if (footprint <= 0) return;

        /* Straight edges: every bay carrying the value puts it on the same shelves. The
           dominant shelf set is the band the merchant would draw; a bay that stops short or
           runs on costs the difference. */
        const byBay = groupBy(runMembers, (s) => String(s.bay));
        const setCounts = new Map<string, number>();
        byBay.forEach((bayMembers) => {
          const signature = [...new Set(bayMembers.map(bayRow))].sort((a, b) => a - b).join(",");
          setCounts.set(signature, (setCounts.get(signature) ?? 0) + 1);
        });
        let dominant = "";
        let dominantCount = -1;
        [...setCounts.entries()]
          .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
          .forEach(([signature, count]) => {
            if (count > dominantCount) {
              dominantCount = count;
              dominant = signature;
            }
          });
        const dominantSet = new Set(dominant ? dominant.split(",").map(Number) : []);
        let straightWeighted = 0;
        let straightWeight = 0;
        byBay.forEach((bayMembers) => {
          const own = new Set(bayMembers.map(bayRow));
          const union = new Set([...own, ...dominantSet]);
          let shared = 0;
          own.forEach((shelf) => {
            if (dominantSet.has(shelf)) shared += 1;
          });
          const bayFootprint = bayMembers.reduce((sum, s) => sum + s.width, 0);
          straightWeighted += (union.size > 0 ? shared / union.size : 1) * bayFootprint;
          straightWeight += bayFootprint;
        });
        const score = straightWeight > 0 ? straightWeighted / straightWeight : 1;
        weighted += score * footprint * level.weight;
        weight += footprint * level.weight;
        straightTotal += score * footprint * level.weight;
        if (score < 1) {
          breaks.push({
            block: key,
            shelves,
            score: round3(score),
            footprintCm: +footprint.toFixed(1),
          });
        }
      });
    });
  }

  const worst = breaks
    .sort(
      (a, b) =>
        (1 - b.score) * b.footprintCm - (1 - a.score) * a.footprintCm ||
        (a.block < b.block ? -1 : a.block > b.block ? 1 : 0),
    )
    .slice(0, 3);

  const edges = weight > 0 ? weighted / weight : 1;
  /* A configured ladder and the band edges each say something the other does not, so the
     component is their mean; with no ladder the edges carry it alone. */
  return {
    graded: weight > 0 || ladder !== null,
    sub: {
      score: ladder ? (edges + ladder.score) / 2 : edges,
      detail: {
        bands: breaks.length,
        straightEdges: weight > 0 ? round3(straightTotal / weight) : 1,
        footprintCm: +weight.toFixed(0),
        ...(ladder ? { tierLadder: round3(ladder.score), ...ladder.detail } : {}),
      },
    },
    worst,
  };
};

/* ---------------------------------------------------------------- contiguity */

interface ContiguityBreak {
  block: string;
  stretches: number;
  footprintCm: number;
}

interface XWindow {
  from: number;
  to: number;
}

/** The x-windows the members fall into on ONE shelf, once overlaps and touches are merged.
 *  Air between two pieces does not split them: only a product of another value sitting in
 *  the gap (one of `lane`, the shelf's other stretches), or a bay with no board on this row
 *  (`boardAt` differs), makes the shopper meet it twice. */
const shelfWindowsOf = (
  onShelf: readonly Stretch[],
  lane: readonly Stretch[],
  boardAt: (x: number) => number,
  eps: number,
): XWindow[] => {
  const members = new Set(onShelf);
  const others = lane.filter((stretch) => !members.has(stretch));
  const intervals = onShelf
    .map((member) => ({ from: member.x, to: member.x + member.width }))
    .sort((a, b) => a.from - b.from);
  const windows: XWindow[] = [];
  for (const interval of intervals) {
    const last = windows[windows.length - 1];
    const inGap = (other: Stretch) =>
      !!last && Math.min(interval.from, other.x + other.width) - Math.max(last.to, other.x) > eps;
    const sameBoard = !!last && boardAt(last.from) === boardAt(interval.from);
    if (last && (interval.from <= last.to + eps || (sameBoard && !others.some(inGap)))) {
      last.to = Math.max(last.to, interval.to);
    } else windows.push({ ...interval });
  }
  return windows;
};

/** Columns: windows from different shelves belong together only where they overlap in x. */
const columnsOf = (windows: readonly XWindow[], eps: number): number => {
  const parent = windows.map((_, index) => index);
  const rootOf = (index: number): number =>
    parent[index] === index ? index : (parent[index] = rootOf(parent[index]));
  for (let a = 0; a < windows.length; a++) {
    for (let b = a + 1; b < windows.length; b++) {
      const overlap =
        Math.min(windows[a].to, windows[b].to) - Math.max(windows[a].from, windows[b].from);
      if (overlap > eps) parent[rootOf(a)] = rootOf(b);
    }
  }
  return new Set(windows.map((_, index) => rootOf(index))).size;
};

/**
 * One value, one block. Graded directly rather than inferred from edges: the members of a
 * value are located in their parent's reading order, and a value whose facings fall into two
 * separate runs of that order has been met twice by the shopper. A vertical level reads across
 * the aisle first, a band reads along the shelf first.
 *
 * A stripe is one block only when its shelves line up into ONE column: the windows it owns on
 * neighbouring shelves overlap in x. A brand at bay 1 on one shelf and bay 2 on the next is two
 * columns, however tidy each shelf looks on its own. A brand met twice along one shelf, with
 * another brand between its pieces, is worse again and is charged
 * `rules.contiguityInterleaveFactor` times over.
 *
 * Each break is charged on its own. A footprint-weighted mean let one brand in pieces hide
 * behind twenty tidy ones; the shopper who met that brand twice was not consoled by the others.
 * `rules.contiguityBreakCost` says how hard a break hits.
 */
const scoreContiguity = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
  scopes: LevelScopes,
  bayRow: (stretch: Stretch) => number,
  runById: ReadonlyMap<number, StudioRunGeometry>,
  eps: number,
): { sub: SubScore; worst: ContiguityBreak[]; graded: boolean } => {
  const levels = gradedLevelsOf(rules);
  if (levels.length === 0) {
    return { graded: false, sub: { score: 1, detail: { values: 0 } }, worst: [] };
  }
  const order = levels.map((level) => level.attr);
  const lanes = groupBy(stretches, (s) => laneKey(s.run, s.shelf));
  const breaks: ContiguityBreak[] = [];
  let values = 0;
  let penalty = 0;

  for (const level of levels) {
    const depth = order.indexOf(level.attr);
    const ownPath = order.slice(0, depth + 1);

    const byValue = groupBy(stretches, (s) => `${s.run}␟${pathKey(s, ownPath)}`);
    byValue.forEach((members) => {
      const footprint = members.reduce((sum, s) => sum + s.width, 0);
      if (footprint <= 0) return;
      values += 1;

      /* A stripe is one block when its shelves line up into one column and no shelf meets it
         twice. A band is one block when the shelves it owns run together. Each level is counted
         on the axis it is meant to occupy, so a band is never marked down for the shelf it
         shares with a neighbour and a stripe is never marked down for spanning shelves. */
      let pieces: number;
      let metTwice = 1;
      if (blockingAt(rules, level, scopes, members[0].item.skuId) === "vertical") {
        const windows: XWindow[] = [];
        groupBy(members, (member) => String(member.shelf)).forEach((onShelf) => {
          const { run, shelf } = onShelf[0];
          const own = shelfWindowsOf(
            onShelf,
            lanes.get(laneKey(run, shelf)) ?? [],
            (x) => boardIndexAt(runById.get(run), shelf, x),
            eps,
          );
          metTwice = Math.max(metTwice, own.length);
          windows.push(...own);
        });
        pieces = Math.max(1, columnsOf(windows, eps));
      } else {
        const shelves = [...new Set(members.map(bayRow))].sort((a, b) => a - b);
        pieces = 1;
        for (let index = 1; index < shelves.length; index++) {
          if (shelves[index] !== shelves[index - 1] + 1) pieces += 1;
        }
      }

      const breakUnits = pieces - 1 + (metTwice - 1) * rules.contiguityInterleaveFactor;
      if (breakUnits > 0) {
        penalty += breakUnits * level.weight;
        breaks.push({
          block: pathKey(members[0], ownPath),
          stretches: Math.max(pieces, metTwice),
          footprintCm: +footprint.toFixed(1),
        });
      }
    });
  }

  const worst = breaks
    .sort(
      (a, b) =>
        b.stretches * b.footprintCm - a.stretches * a.footprintCm ||
        (a.block < b.block ? -1 : a.block > b.block ? 1 : 0),
    )
    .slice(0, 3);

  return {
    graded: values > 0,
    sub: {
      score: values > 0 ? Math.max(0, 1 - rules.contiguityBreakCost * penalty) : 1,
      detail: { values, broken: breaks.length, penalty: +penalty.toFixed(3) },
    },
    worst,
  };
};

/** The tier owning the most width, and its share; untiered stretches are excluded. */
const dominantTier = (
  list: readonly Stretch[],
  tierOf: (stretch: Stretch) => number | null,
): { tier: number | null; share: number; width: number } => {
  const widths = new Map<number, number>();
  let total = 0;
  for (const stretch of list) {
    const tier = tierOf(stretch);
    if (tier === null) continue;
    widths.set(tier, (widths.get(tier) ?? 0) + stretch.width);
    total += stretch.width;
  }
  let best: number | null = null;
  let bestWidth = 0;
  widths.forEach((width, tier) => {
    if (width > bestWidth) {
      bestWidth = width;
      best = tier;
    }
  });
  return { tier: best, share: total > 0 ? bestWidth / total : 1, width: total };
};

/**
 * The declared tier ladder, where the merchant configured one: each shelf reads as one tier
 * across the aisle, a tier boundary falls on a shelf line rather than inside a shelf, and the
 * tiers stack down a column in the declared order. Null when no ladder is configured or no placed
 * pack carries a tier it names, and the band edges then carry the component alone rather than it
 * being handed a free 1.0.
 */
const scoreTierLadder = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
): SubScore | null => {
  if (!rules.tierAttr || rules.tierOrder.length === 0) return null;
  const tierOf = tierIndexer(rules);

  /* a · aisle-wide purity of each shelf. */
  const lanes = lanesOf(stretches);
  let purityWeighted = 0;
  let purityWeight = 0;
  lanes.forEach((list) => {
    const dominant = dominantTier(list, tierOf);
    purityWeighted += dominant.share * dominant.width;
    purityWeight += dominant.width;
  });
  if (purityWeight === 0) return null;
  const aisleTierPurity = purityWeighted / purityWeight;

  /* b+c · per column (a vertical brand block), one lane at a time. */
  const columns = groupBy(stretches, (s) => `${s.run}|${s.attrs[rules.brandAttr] ?? "—"}`);
  let boundaryWeighted = 0;
  let boundaryWeight = 0;
  let orderedColumns = 0;
  let gradedColumns = 0;

  columns.forEach((members) => {
    const byShelf = groupBy(members, (s) => String(s.shelf));
    const shelfEntries = [...byShelf.entries()].sort((a, b) => Number(a[0]) - Number(b[0]));
    for (const [, list] of shelfEntries) {
      const dominant = dominantTier(list, tierOf);
      boundaryWeighted += dominant.share * dominant.width;
      boundaryWeight += dominant.width;
    }
    if (shelfEntries.length < 2) return;
    // Clean stacking: tiers never go backwards and none returns after another.
    const reading = shelfEntries
      .map(([, list]) => dominantTier(list, tierOf).tier)
      .filter((tier): tier is number => tier !== null);
    if (reading.length < 2) return;
    gradedColumns++;
    let clean = true;
    const seen = new Set<number>();
    let previous = -1;
    let previousTier = -1;
    for (const tier of reading) {
      if (tier !== previousTier && seen.has(tier)) clean = false;
      if (tier < previous) clean = false;
      previous = Math.max(previous, tier);
      seen.add(tier);
      previousTier = tier;
    }
    if (clean) orderedColumns++;
  });

  const columnBoundary = boundaryWeight > 0 ? boundaryWeighted / boundaryWeight : 1;
  const columnTierOrder = gradedColumns > 0 ? orderedColumns / gradedColumns : 1;

  return {
    score: (aisleTierPurity + columnBoundary + columnTierOrder) / 3,
    detail: {
      aisleTierPurity: round3(aisleTierPurity),
      columnBoundary: round3(columnBoundary),
      columnTierOrder: round3(columnTierOrder),
      graded: gradedColumns,
    },
  };
};

/* ---------------------------------------------------------------- cdt */

const tierIndexer = (rules: AestheticsRules) => {
  const byValue = new Map<string, number>();
  rules.tierOrder.forEach((tier, index) => {
    for (const value of tier.values) byValue.set(value.trim().toLowerCase(), index);
  });
  return (stretch: Stretch): number | null => {
    if (!rules.tierAttr) return null;
    const value = (stretch.attrs[rules.tierAttr] ?? "").trim().toLowerCase();
    return byValue.has(value) ? byValue.get(value)! : null;
  };
};

const scoreDetector = (
  flags: readonly Flag[],
  skuCount: number,
  rules: AestheticsRules,
): SubScore => {
  const penalty = flags.reduce((sum, flag) => sum + (rules.severityPenalty[flag.severity] ?? 0), 0);
  const perHundred = skuCount > 0 ? (penalty * 100) / skuCount : 0;
  return {
    score: clamp01(1 - perHundred / rules.flagsPerHundredZero),
    detail: {
      flags: flags.length,
      weightedPenalty: +penalty.toFixed(2),
      perHundredSkus: +perHundred.toFixed(2),
    },
  };
};

/** Ordering consistency: each measure is a fraction of pairs (or blocks) that read correctly. */
const scoreOrdering = (
  stretches: readonly Stretch[],
  rules: AestheticsRules,
  eps: number,
): { sub: SubScore; breaks: OrderingBreak[]; graded: boolean } => {
  const breaks: OrderingBreak[] = [];
  // Every block a rule caught, most violations first: `flagged` lists them all, `worst` the first.
  const byBlockWorstFirst = (byBlock: ReadonlyMap<string, number>) =>
    [...byBlock.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  const lanes = lanesOf(stretches);
  const identity = gradedLevelsOf(rules).map((level) => level.attr);
  const blockKey = (s: Stretch) => pathKey(s, identity);

  /* a · sizes run one way within a block, along the shelf. Either way is orderly: a block that
     steps down in size reads as cleanly as one that steps up, and only a block that goes up and
     down again is marked. A shuffle splits its pairs evenly both ways and scores near zero. */
  let sizePairs = 0;
  const sizeBadByBlock = new Map<string, number>();
  const sizeVotes = new Map<string, { up: number; down: number }>();
  lanes.forEach((list) => {
    for (let i = 1; i < list.length; i++) {
      const a = list[i - 1];
      const b = list[i];
      if (blockKey(a) !== blockKey(b)) continue;
      const left = sizeMagnitude(a.attrs[rules.sizeAttr]);
      const right = sizeMagnitude(b.attrs[rules.sizeAttr]);
      if (!left || !right || left.family !== right.family) continue;
      if (left.magnitude === right.magnitude) continue;
      sizePairs++;
      const key = `${a.run}␟${blockKey(a)}`;
      const votes = sizeVotes.get(key) ?? { up: 0, down: 0 };
      if (left.magnitude < right.magnitude) votes.up++;
      else votes.down++;
      sizeVotes.set(key, votes);
    }
  });
  sizeVotes.forEach((votes, key) => {
    const against = Math.min(votes.up, votes.down);
    if (against > 0) {
      sizeBadByBlock.set(key.split("␟").slice(1).join("␟"), against);
    }
  });
  const sizeOrder = orderlinessBeyondChance(
    [...sizeVotes.values()].map((votes) => ({ ascending: votes.up, descending: votes.down })),
  );
  const sizeAscending = sizeOrder.score;
  for (const [block, count] of byBlockWorstFirst(sizeBadByBlock)) {
    breaks.push({
      rule: "size ascending",
      block,
      detail: `${count} ${count === 1 ? "place" : "places"} where sizes turn back against the block's own direction along the shelf`,
      violations: count,
    });
  }

  /* b · the tier ladder reads in declared order along the shelf. */
  const tierBlockAttrs = identity.slice(0, Math.max(0, identity.indexOf(rules.tierAttr)));
  const tierBlockKey = (s: Stretch) =>
    pathKey(s, tierBlockAttrs.length ? tierBlockAttrs : [rules.brandAttr]);
  const tierOf = tierIndexer(rules);
  let tierPairs = 0;
  let tierBad = 0;
  const tierBadByBlock = new Map<string, number>();
  if (rules.tierAttr && rules.tierOrder.length > 0) {
    lanes.forEach((list) => {
      for (let i = 1; i < list.length; i++) {
        const a = list[i - 1];
        const b = list[i];
        if (tierBlockKey(a) !== tierBlockKey(b)) continue;
        const left = tierOf(a);
        const right = tierOf(b);
        if (left === null || right === null || left === right) continue;
        tierPairs++;
        if (left > right) {
          tierBad++;
          tierBadByBlock.set(tierBlockKey(a), (tierBadByBlock.get(tierBlockKey(a)) ?? 0) + 1);
        }
      }
    });
  }
  const tierLadder = tierPairs > 0 ? 1 - tierBad / tierPairs : 1;
  for (const [block, count] of byBlockWorstFirst(tierBadByBlock)) {
    breaks.push({
      rule: "handle tier order",
      block,
      detail: `${count} pair(s) put a later handle tier before an earlier one`,
      violations: count,
    });
  }

  /* c · `lastInBlock` values close their block rather than standing inside it. */
  const isLastValue = (stretch: Stretch) => {
    const haystack = [stretch.attrs[rules.flavourAttr], stretch.item.name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return rules.lastInBlock.some((needle) => haystack.includes(needle.toLowerCase()));
  };
  const readingIndex = new Map<Stretch, number>();
  {
    const ordered = [...stretches].sort((a, b) => a.run - b.run || a.shelf - b.shelf || a.x - b.x);
    ordered.forEach((stretch, index) => readingIndex.set(stretch, index));
  }
  const blocksForLast = groupBy(stretches, (s) => `${laneKey(s.run, s.shelf)}|${blockKey(s)}`);
  let lastBlocks = 0;
  let lastOk = 0;
  const earlyByBlock = new Map<string, number>();
  blocksForLast.forEach((members, key) => {
    const marked = members.filter(isLastValue);
    if (!marked.length || marked.length === members.length) return;
    lastBlocks++;
    const rightmostOther = Math.max(
      ...members.filter((s) => !isLastValue(s)).map((s) => s.x + s.width),
    );
    const early = marked.filter((s) => s.x + s.width < rightmostOther - eps);
    if (early.length === 0) lastOk++;
    else {
      const block = key.split("|").slice(2).join("|");
      earlyByBlock.set(block, (earlyByBlock.get(block) ?? 0) + early.length);
    }
  });
  const sensitiveLast = lastBlocks > 0 ? lastOk / lastBlocks : 1;
  for (const [block, count] of byBlockWorstFirst(earlyByBlock)) {
    breaks.push({
      rule: "attribute reads last",
      block,
      detail: `${count} designated end-value facing(s) stand inside the block instead of closing it`,
      violations: count,
    });
  }

  /* d · the same flavour ribbon recurs brand to brand. */
  const flavourByBrand = new Map<string, string[]>();
  groupBy(stretches, (s) => String(s.attrs[rules.brandAttr] ?? "—")).forEach((members, brand) => {
    const seen: string[] = [];
    for (const stretch of [...members].sort(
      (a, b) => readingIndex.get(a)! - readingIndex.get(b)!,
    )) {
      const value = stretch.attrs[rules.flavourAttr];
      if (!value) continue;
      if (!seen.includes(value)) seen.push(value);
    }
    if (seen.length >= 2) flavourByBrand.set(brand, seen);
  });
  const pairVotes = new Map<string, { forward: number; backward: number }>();
  flavourByBrand.forEach((sequence) => {
    for (let i = 0; i < sequence.length; i++) {
      for (let j = i + 1; j < sequence.length; j++) {
        const [a, b] = [sequence[i], sequence[j]];
        const forward = a < b;
        const key = forward ? `${a}␟${b}` : `${b}␟${a}`;
        const votes = pairVotes.get(key) ?? { forward: 0, backward: 0 };
        if (forward) votes.forward++;
        else votes.backward++;
        pairVotes.set(key, votes);
      }
    }
  });
  let ribbonPairs = 0;
  let ribbonDisagreements = 0;
  pairVotes.forEach((votes) => {
    ribbonPairs += votes.forward + votes.backward;
    ribbonDisagreements += Math.min(votes.forward, votes.backward);
  });
  const flavourRibbon = ribbonPairs > 0 ? 1 - ribbonDisagreements / ribbonPairs : 1;
  const ribbonBrands = flavourByBrand.size;
  const ribbonAgainst: [string, number][] = [];
  flavourByBrand.forEach((sequence, brand) => {
    let against = 0;
    for (let i = 0; i < sequence.length; i++) {
      for (let j = i + 1; j < sequence.length; j++) {
        const [a, b] = [sequence[i], sequence[j]];
        const forward = a < b;
        const votes = pairVotes.get(forward ? `${a}␟${b}` : `${b}␟${a}`);
        if (!votes) continue;
        const majorityForward = votes.forward >= votes.backward;
        if (forward !== majorityForward) against++;
      }
    }
    if (against > 0) ribbonAgainst.push([brand, against]);
  });
  // A stable sort, so a tie keeps the aisle's brand order as the worst.
  for (const [brand, count] of ribbonAgainst.sort((a, b) => b[1] - a[1])) {
    breaks.push({
      rule: "terminal variant ribbon",
      block: brand,
      detail: `this brand reads ${count} terminal-variant pair(s) the opposite way round from the rest of the aisle`,
      violations: count,
    });
  }

  /* Only the checks that found something to grade are averaged: a plan with no tier ladder or
     no end-of-block values is not handed their 1.0. Size order is reported but not scored: on
     generated plans it reads no better than on the same plan shuffled, so it cannot tell a tidy
     block from a random one. The rank order in blockShape does. */
  const graded = [
    tierPairs > 0 ? tierLadder : null,
    lastBlocks > 0 ? sensitiveLast : null,
    ribbonPairs > 0 ? flavourRibbon : null,
  ].filter((value): value is number => value !== null);
  return {
    graded: graded.length > 0,
    sub: {
      score: graded.length > 0 ? graded.reduce((sum, value) => sum + value, 0) / graded.length : 1,
      detail: {
        sizeAscending: round3(sizeAscending),
        tierLadder: round3(tierLadder),
        sensitiveLast: round3(sensitiveLast),
        flavourRibbon: round3(flavourRibbon),
        sizePairs,
        tierPairs,
        lastBlocks,
        ribbonBrands,
      },
    },
    breaks,
  };
};

/* ---------------------------------------------------------------- fullness */

const windowsOf = (run: StudioRunGeometry, shelf: StudioRunGeometry["shelves"][number]) =>
  rowWindowsOf(run)
    .filter((window) => window.rank === shelf.rank)
    .map((window) => ({
      xCm: window.startCm,
      widthCm: window.endCm - window.startCm,
      bays:
        shelf.spans?.find(
          (span) => span.xCm <= window.startCm && span.xCm + span.widthCm >= window.endCm,
        )?.bays ?? [],
    }));

const baysLabel = (bays: readonly number[]) =>
  bays.length === 0
    ? ""
    : bays.length === 1
      ? ` (bay ${bays[0]})`
      : ` (bays ${bays[0]}-${bays[bays.length - 1]})`;

/** A stretch of shelf a product covers as laid out: its position and drawn width. */
export interface CoveringPiece {
  run: number;
  shelf: number;
  x: number;
  width: number;
}

/** One shelf row window and how much of its width the products leave uncovered. */
export interface RowWindowAir {
  run: number;
  /** The wall row (shelf rank). */
  shelf: number;
  xCm: number;
  widthCm: number;
  /** 1-based bays the window spans; empty where the row runs the whole width. */
  bays: readonly number[];
  coveredCm: number;
  /** Uncovered width over the window's width, 0-1. */
  airShare: number;
}

/**
 * Every shelf row window (`windowsOf`) with the width the pieces leave uncovered, by run, then
 * row, then x. Uncovered width, not summed gaps: an even spread can leave every gap under eps.
 * The shelves-not-full gate fails a window whose `airShare` is over 1 - `approvalFullness`.
 */
export const rowWindowAirOf = (
  runs: readonly StudioRunGeometry[],
  pieces: readonly CoveringPiece[],
  eps: number = epsOf(runs),
): RowWindowAir[] => {
  const lanes = new Map<string, CoveringPiece[]>();
  for (const piece of pieces) {
    if (!(piece.width > 0)) continue;
    const key = laneKey(piece.run, piece.shelf);
    lanes.set(key, [...(lanes.get(key) ?? []), piece]);
  }
  lanes.forEach((list) => list.sort((a, b) => a.x - b.x));
  return runs.flatMap((run) =>
    run.shelves.flatMap((shelf) => {
      const list = lanes.get(laneKey(run.run, shelf.rank)) ?? [];
      return windowsOf(run, shelf).map((window) => {
        const left = window.xCm;
        const right = window.xCm + window.widthCm;
        let cursor = left;
        let coveredCm = 0;
        for (const piece of list) {
          if (!(piece.x + piece.width > left + eps && piece.x < right - eps)) continue;
          coveredCm += Math.max(
            0,
            Math.min(right, piece.x + piece.width) - Math.max(cursor, piece.x),
          );
          cursor = Math.max(cursor, piece.x + piece.width);
        }
        return {
          run: run.run,
          shelf: shelf.rank,
          xCm: window.xCm,
          widthCm: window.widthCm,
          bays: window.bays,
          coveredCm,
          airShare: window.widthCm > 0 ? (window.widthCm - coveredCm) / window.widthCm : 0,
        };
      });
    }),
  );
};

/**
 * Clear height over a stretch and the share of it left empty: from its board's top to the
 * underside of the nearest product-holding board above it in x. Undefined on the top shelf, where
 * nothing caps the product, and where the stacked height is unknown.
 */
const verticalAirOf = (
  run: StudioRunGeometry,
  shelf: StudioRunGeometry["shelves"][number],
  stretch: Stretch,
  eps: number,
): { air: number; excess: number } | undefined => {
  if (stretch.stackHeight === undefined || !(stretch.packHeight > 0)) return undefined;
  let ceiling = Infinity;
  for (const above of run.shelves) {
    // Rank 1 is the top row. A row whose boards all hold no product (spans, none of them) or no
    // row at all (rank 0) is fixture, and never caps; a row without bay data runs the full width.
    if (above.rank < 1 || above.rank >= shelf.rank || above.spans?.length === 0) continue;
    const boards = above.spans?.length ? above.spans : [{ xCm: 0, widthCm: run.widthCm }];
    const overlaps = boards.some(
      (board) =>
        board.xCm < stretch.x + stretch.width - eps && board.xCm + board.widthCm > stretch.x + eps,
    );
    if (overlaps) ceiling = Math.min(ceiling, above.yCm);
  }
  const opening = ceiling - shelf.yCm - shelf.thicknessCm;
  if (!Number.isFinite(opening) || opening <= 0) return undefined;
  const air = Math.max(0, opening - stretch.stackHeight);
  // Less than one more pack is finger room: nothing could fill it.
  return { air: air / opening, excess: Math.max(0, air - stretch.packHeight) / opening };
};

/**
 * Square gaps vs half a bay so one hole costs more than the same air spread thin. Height left
 * under the board above scales that down where any stretch has a board over it, so a wall
 * filled to its boards scores as it would on width alone.
 */
const scoreFullness = (
  stretches: readonly Stretch[],
  runs: readonly StudioRunGeometry[],
  maxAirShare: number,
  eps: number,
): {
  sub: SubScore;
  /** The width score alone: what the shelves-not-full gate judges, since a shelf's height is
   *  often not the plan's to change. */
  horizontal: number;
  emptiest: EmptiestShelf | null;
  airiest: AiriestShelf | null;
  graded: boolean;
  airyRows: string[];
} => {
  const lanes = lanesOf(stretches);
  let verticalExcess = 0;
  let verticalAir = 0;
  let verticalWeight = 0;
  let airiest: AiriestShelf | null = null;
  let weighted = 0;
  let weight = 0;
  let overhangCm = 0;
  let airTotal = 0;
  let totalWidth = 0;
  let emptiest: EmptiestShelf | null = null;
  const airyRows = rowWindowAirOf(runs, stretches, eps)
    .filter((window) => window.airShare > maxAirShare)
    .map(
      (window) =>
        `run ${window.run + 1}, shelf ${window.shelf}${baysLabel(window.bays)}: ${Math.round(100 * window.airShare)}% air`,
    );

  // Untouched shelves are lanes too; skipping them would hide the emptiest.
  for (const run of runs) {
    const gapReference = (run.bayWidthCm || planBayWidthOf(runs)) / 2;
    for (const shelf of run.shelves) {
      const list = lanes.get(laneKey(run.run, shelf.rank)) ?? [];
      const onBoard = new Set<Stretch>();
      for (const window of rowWindowsOf(run).filter((entry) => entry.rank === shelf.rank)) {
        const left = window.startCm;
        const right = window.endCm;
        totalWidth += right - left;
        const inside = list.filter((s) => s.x + s.width > left + eps && s.x < right - eps);
        inside.forEach((stretch) => onBoard.add(stretch));
        const gaps: { size: number; at: number }[] = [];
        let cursor = left;
        for (const stretch of inside) {
          const gap = stretch.x - cursor;
          if (gap > eps) gaps.push({ size: gap, at: cursor });
          cursor = Math.max(cursor, stretch.x + stretch.width);
        }
        const tail = right - cursor;
        if (tail > eps) gaps.push({ size: tail, at: cursor });
        if (cursor > right + eps) overhangCm += cursor - right;
        const firstStart = inside.length ? Math.min(...inside.map((s) => s.x)) : left;
        if (firstStart < left - eps) overhangCm += left - firstStart;
        const air = gaps.reduce((sum, gap) => sum + gap.size, 0);
        airTotal += air;
        const penalty = gaps.reduce(
          (sum, gap) => sum + (gap.size * gap.size) / (gapReference * gapReference),
          0,
        );
        const laneScore = clamp01(1 - penalty);
        weighted += laneScore * (right - left);
        weight += right - left;
        const largest = gaps.reduce((best, gap) => (gap.size > best.size ? gap : best), {
          size: 0,
          at: 0,
        });
        if (!emptiest || largest.size > emptiest.largestGapCm) {
          emptiest = {
            run: run.run,
            shelf: shelf.rank,
            largestGapCm: +largest.size.toFixed(1),
            gapAtXCm: +largest.at.toFixed(1),
            totalAirCm: +air.toFixed(1),
          };
        }
      }
      let rowExcess = 0;
      let rowWeight = 0;
      for (const stretch of onBoard) {
        const vertical = verticalAirOf(run, shelf, stretch, eps);
        if (!vertical) continue;
        rowExcess += clamp01(vertical.excess) * stretch.width;
        rowWeight += stretch.width;
        verticalAir += clamp01(vertical.air) * stretch.width;
      }
      verticalExcess += rowExcess;
      verticalWeight += rowWeight;
      if (rowWeight > 0 && (!airiest || rowExcess / rowWeight > airiest.airShare)) {
        airiest = {
          run: run.run,
          shelf: shelf.rank,
          airShare: +(rowExcess / rowWeight).toFixed(4),
        };
      }
    }
  }

  const gapScore = weight > 0 ? weighted / weight : 1;
  // Charged against a bay width: a bay's worth of overhang wipes the component out.
  const overhangPenalty = clamp01(overhangCm / planBayWidthOf(runs));
  const horizontal = clamp01(gapScore - overhangPenalty);
  const verticalScore = verticalWeight > 0 ? 1 - verticalExcess / verticalWeight : 1;

  return {
    graded: weight > 0,
    sub: {
      score: verticalWeight > 0 ? horizontal * verticalScore : horizontal,
      detail: {
        gapScore: round3(gapScore),
        overhangCm: +overhangCm.toFixed(1),
        totalAirCm: +airTotal.toFixed(1),
        airShare: totalWidth > 0 ? +(airTotal / totalWidth).toFixed(4) : 0,
        verticalScore: round3(verticalScore),
        verticalAirShare: verticalWeight > 0 ? +(verticalAir / verticalWeight).toFixed(4) : 0,
      },
    },
    horizontal,
    emptiest,
    airiest,
    airyRows,
  };
};

/* ---------------------------------------------------------------- shelf sense */

/**
 * Big under small, heavy low. Both grade something out of the box: with no stated pounds the
 * heaviest share of the plan's own packs are the heavy ones, and pack height and pack size are
 * read off the plan, so a category that has never configured a threshold is still graded.
 */
const scoreShelfSense = (
  stretches: readonly Stretch[],
  runs: readonly StudioRunGeometry[],
  rules: AestheticsRules,
): { sub: SubScore; graded: boolean } => {
  const lowerRanks = new Map<number, Set<number>>();
  for (const run of runs) {
    const ranks = run.shelves.map((shelf) => shelf.rank).sort((a, b) => a - b);
    if (ranks.length === 0) continue;
    const lowCount = Math.max(1, Math.round(ranks.length * rules.lowerShelfShare));
    lowerRanks.set(run.run, new Set(ranks.slice(ranks.length - lowCount)));
  }

  /* a · heavy low. */
  const weighed = stretches.map((stretch) => ({
    stretch,
    pounds: countedWeightPounds(
      {
        name: stretch.item.name,
        size: stretch.attrs[rules.sizeAttr] ?? "",
        unitWeight: stretch.item.weightKg,
        weightUom: stretch.item.weightKg === undefined ? null : "kg",
      },
      "UNIT",
    ),
  }));
  const known = weighed.filter(
    (entry): entry is { stretch: Stretch; pounds: number } => entry.pounds !== null,
  );
  let threshold = rules.bulkPounds;
  /* A stated threshold is inclusive: a pack AT the merchant's number is heavy. */
  let isHeavy = (pounds: number) => threshold > 0 && pounds >= threshold;
  if (!(threshold > 0) && known.length > 0) {
    /* No stated threshold: the heaviest share of this plan's own packs are the heavy ones —
       the packs strictly heavier than the first pack past that share. A weight shared across
       the cut is not "the heaviest share" (weights read off size are often shared by most of
       the plan), so it is never swept in whole, and a plan whose packs all weigh the same has
       nothing heavy to grade. */
    const sorted = [...known].map((entry) => entry.pounds).sort((a, b) => b - a);
    const index = Math.min(
      sorted.length - 1,
      Math.max(0, Math.floor(sorted.length * rules.heavyShare)),
    );
    threshold = sorted[index];
    isHeavy = (pounds: number) => pounds > threshold;
  }
  let bulkLow = 0;
  let bulkTotal = 0;
  let bulkFacings = 0;
  for (const entry of known) {
    if (!isHeavy(entry.pounds)) continue;
    bulkTotal += entry.stretch.width;
    bulkFacings += entry.stretch.hFacings;
    if (lowerRanks.get(entry.stretch.run)?.has(entry.stretch.shelf)) bulkLow += entry.stretch.width;
  }
  const heavyLow = bulkTotal > 0 ? bulkLow / bulkTotal : null;

  /* b · big under small: inside a block, the lower shelf carries the bigger packs.
     Judged shelf against shelf on each shelf's typical pack rather than pack against pack,
     which keeps it linear — this runs inside the move loop, where a pairwise sweep over a
     whole aisle costs minutes. */
  const identity = gradedLevelsOf(rules).map((level) => level.attr);
  const median = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  };
  let bigPairs = 0;
  let bigGood = 0;
  const byBlock = groupBy(stretches, (s) => `${s.run}␟${pathKey(s, identity)}`);
  byBlock.forEach((members) => {
    const byShelf = groupBy(members, (s) => String(s.shelf));
    if (byShelf.size < 2) return;
    /* One size family per block, so a count and a volume are never ranked against each other. */
    const families = new Map<string, number>();
    for (const member of members) {
      const size = sizeMagnitude(member.attrs[rules.sizeAttr]);
      if (size) families.set(size.family, (families.get(size.family) ?? 0) + member.width);
    }
    const family = [...families.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))[0];
    const shelfRows = [...byShelf.entries()]
      .map(([shelf, list]) => {
        const heights = list.map((s) => s.item.heightCm).filter((height) => height > 0);
        const sizes = family
          ? list
              .map((s) => sizeMagnitude(s.attrs[rules.sizeAttr]))
              .filter((size): size is { family: string; magnitude: number } =>
                Boolean(size && size.family === family[0]),
              )
              .map((size) => size.magnitude)
          : [];
        return {
          shelf: Number(shelf),
          height: heights.length ? median(heights) : null,
          size: sizes.length ? median(sizes) : null,
        };
      })
      .sort((a, b) => a.shelf - b.shelf);

    for (let index = 1; index < shelfRows.length; index++) {
      const above = shelfRows[index - 1];
      const below = shelfRows[index];
      const compare = (high: number | null, low: number | null) => {
        if (high === null || low === null || high === low) return null;
        return low > high;
      };
      const verdict = compare(above.height, below.height) ?? compare(above.size, below.size);
      if (verdict === null) continue;
      bigPairs++;
      if (verdict) bigGood++;
    }
  });
  const bigUnderSmall = bigPairs > 0 ? bigGood / bigPairs : null;

  const parts: { score: number; weight: number }[] = [];
  if (heavyLow !== null) parts.push({ score: heavyLow, weight: rules.shelfSenseSplit.heavyLow });
  if (bigUnderSmall !== null) {
    parts.push({ score: bigUnderSmall, weight: rules.shelfSenseSplit.bigUnderSmall });
  }
  const totalWeight = parts.reduce((sum, part) => sum + part.weight, 0);

  return {
    graded: parts.length > 0,
    sub: {
      score:
        totalWeight > 0
          ? parts.reduce((sum, part) => sum + part.score * part.weight, 0) / totalWeight
          : 1,
      detail: {
        bulkFacings,
        bulkWidthCm: +bulkTotal.toFixed(1),
        bulkLowShare: heavyLow === null ? 1 : round3(heavyLow),
        heavyPounds: +threshold.toFixed(2),
        bigUnderSmall: bigUnderSmall === null ? 1 : round3(bigUnderSmall),
        bigPairs,
      },
    },
  };
};

/* ---------------------------------------------------------------- rules kept */

const conditionIsFence = (condition: BandRule["condition"]) => condition === "MUST_NOT_HAVE";
const conditionIsRequired = (condition: BandRule["condition"]) =>
  condition === "MUST_HAVE" || condition === "MUST_HAVE_ONLY";

/**
 * Did the plan obey the bands the merchant accepted? A MUST_NOT_HAVE value has no facings in
 * the cells it is fenced out of, a MUST_HAVE value has at least one in every cell it owns (each
 * bay its own cell), MUST_HAVE_ONLY also admits nothing else there, and MAY_HAVE says nothing. Breaches are counted in facings, so one stray facing costs
 * less than a whole block in the wrong place.
 *
 * A rule whose value could not be matched to any SKU is NOT graded and NOT guessed at: it is
 * counted in `notGraded` and reported, because a band label is a promoted name that need not
 * match any single column.
 */
const scoreRulesKept = (
  stretches: readonly Stretch[],
  bands: readonly BandRule[] | undefined,
): { sub: SubScore; worst: RuleBreak[]; breaks: RuleBreak[]; graded: boolean } => {
  if (!bands || bands.length === 0) {
    return { graded: false, sub: { score: 1, detail: { rules: 0 } }, worst: [], breaks: [] };
  }
  const notGraded = bands.filter((band) => !band.resolved || band.skuIds.length === 0).length;
  const gradable = bands.filter(
    (band) =>
      band.resolved &&
      band.skuIds.length > 0 &&
      band.cells.length > 0 &&
      (conditionIsFence(band.condition) || conditionIsRequired(band.condition)),
  );
  if (gradable.length === 0) {
    return {
      graded: false,
      sub: { score: 1, detail: { rules: bands.length, notGraded, graded: 0 } },
      worst: [],
      breaks: [],
    };
  }

  const breaks: RuleBreak[] = [];
  let breach = 0;
  let exposure = 0;

  for (const band of gradable) {
    const governed = new Set(band.skuIds);
    const members = stretches.filter((stretch) => governed.has(stretch.item.skuId));
    const covers = (cell: BandRule["cells"][number], stretch: Stretch) =>
      (cell.run === undefined || cell.run === stretch.run) &&
      cell.shelf === stretch.shelf &&
      (cell.bays.length === 0 || cell.bays.includes(stretch.bay));
    const inCell = (stretch: Stretch) => band.cells.some((cell) => covers(cell, stretch));

    if (conditionIsFence(band.condition)) {
      /* Exposure is every facing the rule governs: a rule kept perfectly still counts, so a
         plan cannot buy marks by having nothing the rule applies to. */
      exposure += members.reduce((sum, stretch) => sum + stretch.hFacings, 0);
      for (const stretch of members) {
        if (!inCell(stretch)) continue;
        breach += stretch.hFacings;
        breaks.push({
          band: band.label,
          condition: band.condition,
          productName: stretch.item.name,
          shelf: stretch.shelf,
          facings: stretch.hFacings,
        });
      }
      continue;
    }

    /* MUST_HAVE: every fenced cell shows the value. A cell is worth the mean facings a cell of
       this rule carries, so a required value missing from a cell costs what it would have. */
    const required = band.cells.flatMap((cell) =>
      cell.bays.length > 0 ? cell.bays.map((bay) => ({ ...cell, bays: [bay] })) : [cell],
    );
    const present = members.filter(inCell);
    const perCell = present.length
      ? present.reduce((sum, stretch) => sum + stretch.hFacings, 0) / required.length
      : 1;
    // A placement that names no board (a drafted layout) keeps any board of the cell.
    const onBoard = (cell: (typeof required)[number], stretch: Stretch) =>
      cell.board === undefined || stretch.board === undefined || cell.board === stretch.board;
    for (const cell of required) {
      exposure += perCell;
      if (members.some((stretch) => covers(cell, stretch) && onBoard(cell, stretch))) continue;
      breach += perCell;
      breaks.push({
        band: band.label,
        condition: band.condition,
        productName: band.label,
        shelf: cell.shelf,
        facings: 0,
      });
    }

  }

  /* MUST_HAVE_ONLY: "only the listed variants" (the pogger's ParseVariantRestrictionService).
     Every MUST_HAVE_ONLY rule on a cell lists into ONE allow-list, so Gain-only and Tide-only on
     the same shelf admit both and nothing else. Each facing in an exclusive cell is exposed once,
     however many rules cover it. */
  const exclusive = gradable.filter((band) => band.condition === "MUST_HAVE_ONLY");
  for (const stretch of stretches) {
    const covering = exclusive.filter((band) =>
      band.cells.some(
        (cell) =>
          (cell.run === undefined || cell.run === stretch.run) &&
          cell.shelf === stretch.shelf &&
          (cell.bays.length === 0 || cell.bays.includes(stretch.bay)),
      ),
    );
    if (covering.length === 0) continue;
    exposure += stretch.hFacings;
    if (covering.some((band) => band.skuIds.includes(stretch.item.skuId))) continue;
    breach += stretch.hFacings;
    breaks.push({
      band: covering.map((band) => band.label).join(" + "),
      condition: "MUST_HAVE_ONLY",
      productName: stretch.item.name,
      shelf: stretch.shelf,
      facings: stretch.hFacings,
    });
  }

  breaks.sort(
    (a, b) =>
      b.facings - a.facings ||
      a.shelf - b.shelf ||
      (a.productName < b.productName ? -1 : a.productName > b.productName ? 1 : 0),
  );

  return {
    graded: true,
    sub: {
      score: exposure > 0 ? clamp01(1 - breach / exposure) : 1,
      detail: {
        rules: bands.length,
        graded: gradable.length,
        notGraded,
        brokenFacings: +breach.toFixed(1),
        governedFacings: +exposure.toFixed(1),
      },
    },
    worst: breaks.slice(0, 3),
    breaks,
  };
};

/* ---------------------------------------------------------------- range */

const scoreRange = (
  stretches: readonly Stretch[],
  items: readonly StudioItem[],
  draft: StudioDraft,
  rules: AestheticsRules,
  rangedSkuIds: readonly string[] | undefined,
  unplacedAttrs: AestheticsInput["unplacedAttrs"],
  coreRange?: CoreRange,
): { sub: SubScore; graded: boolean; unplaced: string[]; eliminated: string[] } => {
  const placed = new Set(stretches.map((stretch) => stretch.item.skuId));
  const rangeGiven = (rangedSkuIds?.length ?? 0) > 0;
  const range = [...new Set(rangeGiven ? rangedSkuIds : items.map((item) => item.skuId))];
  const unplaced = range.filter((skuId) => !placed.has(skuId));

  const brandOf = new Map<string, string>();
  for (const [skuId, attrs] of Object.entries(unplacedAttrs ?? {})) {
    const brand = attrs[rules.brandAttr]?.trim();
    if (brand) brandOf.set(skuId, brand);
  }
  for (const item of items) {
    brandOf.set(item.skuId, String(effectiveAttrs(item, draft)[rules.brandAttr] ?? "—"));
  }
  const placedBrands = new Set(
    stretches.map((stretch) => String(stretch.attrs[rules.brandAttr] ?? "—")),
  );
  // No brand or a catch-all (drafted or not) is no brand, so it is neither kept nor dropped.
  const knownBrands = new Set(
    range
      .map((skuId) => brandOf.get(skuId))
      .filter((brand): brand is string => Boolean(brand) && !namesNoBlock(brand)),
  );
  const eliminated = [...knownBrands].filter((brand) => !placedBrands.has(brand)).sort();

  if (!rangeGiven) {
    return { graded: false, sub: { score: 1, detail: { ranged: 0 } }, unplaced, eliminated };
  }
  const skusPlaced = range.length > 0 ? (range.length - unplaced.length) / range.length : 1;
  const brandsKept =
    knownBrands.size > 0 ? (knownBrands.size - eliminated.length) / knownBrands.size : 1;
  const split = rules.rangeSplit;
  const total = split.skusPlaced + split.brandsKept || 1;

  return {
    graded: true,
    sub: {
      score: (skusPlaced * split.skusPlaced + brandsKept * split.brandsKept) / total,
      detail: {
        ranged: range.length,
        unplaced: unplaced.length,
        skusPlaced: round3(skusPlaced),
        brands: knownBrands.size,
        brandsDropped: eliminated.length,
        brandsKept: round3(brandsKept),
        /* With a core range, how many of the unplaced were core (the unplaced check counts
           those) and how many were optional. */
        ...(coreRange
          ? (() => {
              const core = new Set(coreRange.skuIds);
              const coreUnplaced = [...core].filter((skuId) => !placed.has(skuId)).length;
              return {
                coreRange: core.size,
                coreUnplaced,
                optionalUnplaced: unplaced.filter((skuId) => !core.has(skuId)).length,
              };
            })()
          : {}),
      },
    },
    unplaced,
    eliminated,
  };
};

/* ---------------------------------------------------------------- gates */

const evaluateGates = (
  input: AestheticsInput,
  stretches: readonly Stretch[],
  range: { unplaced: readonly string[]; eliminated: readonly string[] },
  rules: AestheticsRules,
  shape: { graded: boolean; broken: readonly BrokenBlock[] },
  rulesKept: { graded: boolean; score: number; worst: readonly RuleBreak[] },
  fullness: { graded: boolean; score: number; airyRows: readonly string[] },
  staircased: readonly StaircasedBlock[],
  stripesGraded: boolean,
): GateResult[] => {
  const { runs, items } = input;
  const eps = epsOf(runs);
  const lanes = lanesOf(stretches);

  const overlaps: string[] = [];
  lanes.forEach((list) => {
    for (let i = 1; i < list.length; i++) {
      const overlap = list[i - 1].x + list[i - 1].width - list[i].x;
      if (overlap > eps) {
        overlaps.push(
          `${list[i - 1].item.name} / ${list[i].item.name} overlap ${lengthLabel(overlap, rules.lengthUnit)} on shelf ${list[i].shelf}`,
        );
      }
    }
  });

  const past: string[] = [];
  const byRun = new Map(runs.map((run) => [run.run, run]));
  for (const stretch of stretches) {
    const run = byRun.get(stretch.run);
    if (!run) continue;
    if (stretch.x < -eps || stretch.x + stretch.width > run.widthCm + eps) {
      past.push(
        `${stretch.item.name} runs to ${lengthLabel(stretch.x + stretch.width, rules.lengthUnit)} of ${lengthLabel(run.widthCm, rules.lengthUnit)} on shelf ${stretch.shelf}`,
      );
    }
  }

  const emptyShelves: string[] = [];
  for (const run of runs) {
    for (const shelf of run.shelves) {
      if (!(lanes.get(laneKey(run.run, shelf.rank))?.length ?? 0)) {
        emptyShelves.push(`run ${run.run + 1}, shelf ${shelf.rank} is empty`);
      }
    }
  }

  const rangeGiven = (input.rangedSkuIds?.length ?? 0) > 0;
  /* Not every product must be placed, only the store's core range. Given one, the check counts
     the core products left off against `unplacedTolerance` of the core range; the rest of the
     range may be dropped. */
  const core = input.coreRange ? new Set(input.coreRange.skuIds) : null;
  const placedSkus = new Set(stretches.map((stretch) => stretch.item.skuId));
  const coreUnplaced = core ? [...core].filter((skuId) => !placedSkus.has(skuId)).sort() : [];
  const allowedUnplaced = Math.floor(
    rules.unplacedTolerance * (core ? core.size : new Set(input.rangedSkuIds ?? []).size),
  );
  // A top-level block in pieces, or met twice along a shelf, is a defect a shopper sees.
  const splitBlocks = shape.broken.filter(
    (block) =>
      block.depth < rules.approvalLevels &&
      (block.pieces > 1 || block.extraRuns > 0) &&
      !isSizeOnlyTier(rules, block),
  );

  // A top-level block in one piece whose outline leaves much of its rectangle empty: an L or a Z.
  const misshapen = shape.broken
    .filter(
      (block) =>
        block.depth === 0 &&
        block.pieces === 1 &&
        block.extraRuns === 0 &&
        block.shapeFill < APPROVAL_BLOCK_FILL &&
        !isSizeOnlyTier(rules, block),
    )
    .sort((a, b) => a.shapeFill - b.shapeFill);

  return [
    {
      id: "overlap",
      label: "No overlapping facings",
      passed: overlaps.length === 0,
      evaluated: true,
      count: overlaps.length,
      examples: overlaps.slice(0, 3),
    },
    {
      id: "past-edge",
      label: "Nothing past the run edge",
      passed: past.length === 0,
      evaluated: true,
      count: past.length,
      examples: past.slice(0, 3),
    },
    {
      id: "empty-shelf",
      label: "No empty shelf",
      passed: emptyShelves.length === 0,
      evaluated: true,
      count: emptyShelves.length,
      examples: emptyShelves.slice(0, 3),
    },
    core
      ? {
          id: "unplaced",
          label:
            input.coreRange?.source === "pog flag"
              ? "Every core-range SKU placed (pog flag)"
              : "Every core-range SKU placed",
          passed: coreUnplaced.length <= allowedUnplaced,
          evaluated: core.size > 0,
          count: coreUnplaced.length,
          examples: coreUnplaced.slice(0, 3),
        }
      : {
          id: "unplaced",
          label: "Every ranged SKU placed",
          passed: range.unplaced.length <= allowedUnplaced,
          evaluated: rangeGiven || items.some((item) => item.placements.length === 0),
          count: range.unplaced.length,
          examples: range.unplaced.slice(0, 3),
        },
    {
      id: "brand-eliminated",
      label: "No brand eliminated",
      passed: range.eliminated.length === 0,
      evaluated: rangeGiven,
      count: range.eliminated.length,
      examples: range.eliminated.slice(0, 3),
    },
    {
      id: "block-in-pieces",
      label: "Every top-level block in one piece",
      passed: splitBlocks.length === 0,
      evaluated: shape.graded,
      count: splitBlocks.length,
      examples: splitBlocks
        .filter(isNamed)
        .slice(0, 3)
        .map((block) => block.block),
    },
    {
      id: "block-staircased",
      label: "Every top-level column straight",
      passed: staircased.length === 0,
      evaluated: stripesGraded,
      count: staircased.length,
      examples: staircased
        .slice(0, 3)
        .map(
          (block) =>
            `${block.block} fills ${Math.round(block.fill * 100)}% of its rectangle, edges ${block.raggedPacks} packs ragged`,
        ),
    },
    {
      id: "block-out-of-shape",
      label: "Every top-level block fills its rectangle",
      passed: misshapen.length === 0,
      evaluated: shape.graded,
      count: misshapen.length,
      examples: misshapen
        .filter(isNamed)
        .slice(0, 3)
        .map((block) => `${block.block} ${Math.round(100 * block.shapeFill)}%`),
    },
    {
      id: "band-broken",
      label:
        input.bandSource === "legacy fences"
          ? "Every accepted band kept (legacy fences)"
          : "Every accepted band kept",
      passed: rulesKept.score >= 0.999,
      evaluated: rulesKept.graded,
      count: rulesKept.score >= 0.999 ? 0 : 1,
      examples:
        rulesKept.score >= 0.999
          ? []
          : rulesKept.worst.map(
              (rule) =>
                `${rule.productName} on shelf ${rule.shelf} (${rule.condition} ${rule.band})`,
            ),
    },
    {
      id: "shelves-not-full",
      label: "Shelves full",
      passed: fullness.score >= rules.approvalFullness && fullness.airyRows.length === 0,
      evaluated: fullness.graded,
      count: fullness.airyRows.length || (fullness.score >= rules.approvalFullness ? 0 : 1),
      examples: fullness.airyRows.slice(0, 3),
    },
  ];
};

/* ---------------------------------------------------------------- the score */

const levelScopesOf = (items: readonly StudioItem[], draft: StudioDraft): LevelScopes => {
  const scopes = new Map<string, Map<string, string>>();
  walkSplitScopes(
    items.map((item) => ({ skuId: item.skuId, attrs: effectiveAttrs(item, draft) })),
    draft.spine,
    draft.scopedLevelOrder,
    (entry, attr) => entry.attrs[attr] ?? "",
    ({ level, path, entries }) => {
      const scope = path.join(PATH_SEPARATOR);
      for (const { skuId } of entries) {
        const byAttr = scopes.get(skuId) ?? new Map<string, string>();
        byAttr.set(level.attr, scope);
        scopes.set(skuId, byAttr);
      }
    },
  );
  return scopes;
};

/** Average the parts that graded something and renormalise the rest away. */
const blend = (parts: readonly { score: number; weight: number; graded: boolean }[]) => {
  const live = parts.filter((part) => part.graded && part.weight > 0);
  const weight = live.reduce((sum, part) => sum + part.weight, 0);
  if (weight <= 0) return { score: 1, graded: false };
  return {
    score: live.reduce((sum, part) => sum + part.score * part.weight, 0) / weight,
    graded: true,
  };
};

export const scorePlanogramAesthetics = (input: AestheticsInput): AestheticsScore => {
  const rules = resolveAestheticsRules(input.draft, input.rules);
  const stretches = toStretches(input.items, input.draft, input.runs);
  const bayWidthCm = planBayWidthOf(input.runs);
  const eps = epsOf(input.runs);

  const scopes = levelScopesOf(input.items, input.draft);
  const boards = onRackBoards(stretches, shapeWallsOf(input.runs), input.runs);
  const vertical = scoreVerticalBlocking(boards.stretches, rules, scopes, bayWidthCm);
  const columns = scoreBrandColumns(
    boards.stretches,
    rules,
    scopes,
    input.contract === "columns",
    eps,
  );
  /* A stripe is straight AND runs the height of its section; either failing breaks it. */
  const stripes = {
    graded: vertical.graded || columns.graded,
    score: (vertical.graded ? vertical.sub.score : 1) * (columns.graded ? columns.sub.score : 1),
  };
  /* Band shape is counted on each bay's own boards: on a ragged wall the wall rows a bay does
     not carry are not gaps the shopper sees. */
  const runById = new Map(input.runs.map((run) => [run.run, run]));
  const bayRow = (stretch: Stretch) =>
    bayShelfOf(runById.get(stretch.run), stretch.bay, stretch.shelf);
  const bands = scoreBands(stretches, rules, scopes, bayRow);
  const contiguity = scoreContiguity(stretches, rules, scopes, bayRow, runById, eps);
  /* Blocking is shape, plus the edge measures of the levels the contract orients: stripes on
     their left and right edges, and (with a spine that bands some levels) bands on their top and
     bottom. Without a spine every level is a stripe and bands stay a diagnostic. */
  const levels = gradedLevelsOf(rules).map(({ attr, weight }) => ({ attr, weight }));
  const shape = scoreBlockShape(
    boards.stretches,
    levels,
    {
      pieceCost: rules.blockPieceCost,
      interleaveFactor: rules.contiguityInterleaveFactor,
      fillFloor: rules.blockFillFloor,
      jitterPacks: rules.jitterPacks,
      skipUnvalued: input.contract === "columns",
    },
    boards.walls,
  );
  const ladder = scoreTierLadder(stretches, rules);
  /* 2D blocking: bands that graded something take half of the edges' weight from the stripes. */
  const bandsGraded =
    bands.graded &&
    gradedLevelsOf(rules).some((level) => levelGradedOn(rules, level, "horizontal", scopes));
  const blockingBlend = blend([
    { score: shape.score, weight: rules.blockingSplit.shape, graded: shape.graded },
    {
      score: stripes.score,
      weight: bandsGraded ? rules.blockingSplit.edges / 2 : rules.blockingSplit.edges,
      graded: stripes.graded,
    },
    { score: bands.sub.score, weight: rules.blockingSplit.edges / 2, graded: bandsGraded },
    {
      score: ladder?.score ?? 1,
      weight: rules.blockingSplit.ladder,
      graded: ladder !== null,
    },
  ]);

  const ordering = scoreOrdering(boards.stretches, rules, eps);
  const childAttrs = levels.map((_, depth) => levels[depth + 1]?.attr ?? input.leafAttr ?? "");
  const blockOrder = scoreBlockOrder(
    boards.stretches,
    levels,
    childAttrs,
    (stretch, attr) => (stretch as Stretch).item.priorities?.[attr],
  );
  const skuCount = new Set(stretches.map((stretch) => stretch.item.skuId)).size;
  const detector = input.flags ? scoreDetector(input.flags, skuCount, rules) : null;
  /* Order inside a block is the rank order plus the configured ordering rules, sharing the
     ordering part of the split; the analyzer's flags keep the detector part. */
  const inBlockOrder = blend([
    { score: blockOrder.score, weight: 0.6, graded: blockOrder.graded },
    { ...ordering.sub, weight: 0.4, graded: ordering.graded },
  ]);
  const cdtBlend = blend([
    { ...inBlockOrder, weight: rules.cdtSplit.ordering },
    { score: detector?.score ?? 1, weight: rules.cdtSplit.detector, graded: detector !== null },
  ]);
  const cdtScore = cdtBlend.score;

  const fullness = scoreFullness(stretches, input.runs, 1 - rules.approvalFullness, eps);
  const shelfSense = scoreShelfSense(stretches, input.runs, rules);
  const rulesKept = scoreRulesKept(stretches, input.bands);
  const range = scoreRange(
    stretches,
    input.items,
    input.draft,
    rules,
    input.rangedSkuIds,
    input.unplacedAttrs,
    input.coreRange,
  );

  const { weights } = rules;
  const components: Record<ComponentKey, ComponentScore> = {
    blocking: {
      score: blockingBlend.score,
      weight: weights.blocking,
      graded: blockingBlend.graded,
      detail: {
        shape: round3(shape.score),
        ...(vertical.graded ? { edges: round3(vertical.sub.score) } : {}),
        ...(columns.graded ? { columns: round3(columns.sub.score) } : {}),
        ...(bandsGraded ? { bands: round3(bands.sub.score) } : {}),
        ...(ladder ? { ladder: round3(ladder.score) } : {}),
      },
    },
    cdt: {
      score: cdtScore,
      weight: weights.cdt,
      graded: cdtBlend.graded,
      detail: {
        ...(blockOrder.graded ? { blockOrder: round3(blockOrder.score) } : {}),
        ...(ordering.graded ? { ordering: round3(ordering.sub.score) } : {}),
        ...(detector ? { detector: round3(detector.score) } : {}),
      },
    },
    fullness: { ...fullness.sub, weight: weights.fullness, graded: fullness.graded },
    rulesKept: { ...rulesKept.sub, weight: weights.rulesKept, graded: rulesKept.graded },
    ergonomics: { ...shelfSense.sub, weight: weights.ergonomics, graded: shelfSense.graded },
    range: { ...range.sub, weight: weights.range, graded: range.graded },
  };

  const live = Object.values(components).filter(
    (component) => component.graded && component.weight > 0,
  );
  const weightSum = live.reduce((sum, component) => sum + component.weight, 0);
  const total = live.reduce((sum, component) => sum + component.score * component.weight, 0);
  const gates = evaluateGates(
    input,
    stretches,
    range,
    rules,
    shape,
    { graded: rulesKept.graded, score: rulesKept.sub.score, worst: rulesKept.worst },
    { graded: fullness.graded, score: fullness.horizontal, airyRows: fullness.airyRows },
    staircasedBlocks(shape.broken, vertical.stripes, rules),
    vertical.stripes.some((stripe) => stripe.depth === 0),
  );
  const gatesPassed = gates.every((gate) => !gate.evaluated || gate.passed);
  const average = weightSum > 0 ? (total / weightSum) * 100 : 0;
  /* Approvable means nothing a person would stop at. A plan that fails a hard check is scaled
     into the band below the approval line rather than clamped to its edge, so it can never be
     approved unseen, and more damage still reads lower among the plans held back. */
  const exactScore = gatesPassed ? average : (average * (rules.approvalThreshold - 1)) / 100;
  const score = Math.round(exactScore);

  /* Reasons, biggest first. Points lost = the share of the 100 this component gave up. */
  const lost = (key: ComponentKey) => {
    const component = components[key];
    if (!component.graded || weightSum <= 0) return 0;
    return +(((1 - component.score) * component.weight * 100) / weightSum).toFixed(1);
  };

  const penalties: AestheticsReason[] = [];
  const pushReason = (
    code: AestheticsReasonCode,
    points: number,
    values: AestheticsReason["values"],
  ) => {
    if (points <= 0) return;
    penalties.push({ code, points, values });
  };

  const blockingLost = lost("blocking");
  if (blockingLost > 0) {
    /* Name what cost the most: the brands off their columns when the stripes lost more than
       block shape did, else the blocks out of shape. */
    const columnsLost = (1 - columns.sub.score) * (vertical.graded ? vertical.sub.score : 1);
    const named = [
      ...new Set(
        [...(columnsLost > 1 - shape.score ? columns.worst : []), ...shape.worst]
          .filter(isNamed)
          .map((entry) => lastSegment(entry.block)),
      ),
    ];
    if (named.length >= 2) {
      pushReason("blockingPair", blockingLost, { first: named[0], second: named[1] });
    } else if (named.length === 1) {
      pushReason("blockingOne", blockingLost, { first: named[0] });
    } else {
      pushReason("blockingPlain", blockingLost, {});
    }
  }

  const rulesLost = lost("rulesKept");
  if (rulesLost > 0 && rulesKept.worst.length > 0) {
    const worst = rulesKept.worst[0];
    pushReason(conditionIsFence(worst.condition) ? "ruleFence" : "ruleMissing", rulesLost, {
      product: worst.productName,
      band: worst.band,
      shelf: worst.shelf,
    });
  } else if (rulesLost > 0) {
    pushReason("rulePlain", rulesLost, {});
  }

  const fullnessLost = lost("fullness");
  if (fullnessLost > 0) {
    const emptiest = fullness.emptiest;
    const { gapScore = 1, verticalScore = 1 } = fullness.sub.detail;
    // A gap names its shelf; height left under the board above has no words of its own yet.
    if (emptiest && emptiest.largestGapCm > 0 && 1 - gapScore >= 1 - verticalScore) {
      pushReason("gap", fullnessLost, {
        shelf: emptiest.shelf,
        gap: wholeLength(emptiest.largestGapCm),
        unit: rules.lengthUnit ?? "in",
      });
    } else {
      pushReason("fullnessPlain", fullnessLost, {});
    }
  }

  const rangeLost = lost("range");
  if (rangeLost > 0) {
    if (range.eliminated.length > 0) {
      pushReason("brandDropped", rangeLost, {
        brand: range.eliminated[0],
        count: range.eliminated.length,
      });
    } else {
      pushReason("unplaced", rangeLost, { count: range.unplaced.length });
    }
  }

  const cdtLost = lost("cdt");
  if (cdtLost > 0) {
    // Size order is reported but not scored, so it never names the points lost here.
    const worstOrdering = [...ordering.breaks]
      .filter((entry) => entry.rule !== "size ascending")
      .sort((a, b) => b.violations - a.violations)[0];
    const worstBlock = blockOrder.worst ?? worstOrdering;
    if (worstBlock && isNamed(worstBlock)) {
      pushReason("orderPlain", cdtLost, { block: lastSegment(worstBlock.block) });
    } else {
      pushReason("cdtPlain", cdtLost, {});
    }
  }

  const shelfSenseLost = lost("ergonomics");
  if (shelfSenseLost > 0) {
    const detail = shelfSense.sub.detail;
    pushReason(
      (detail.bulkLowShare ?? 1) <= (detail.bigUnderSmall ?? 1) ? "heavyHigh" : "bigOverSmall",
      shelfSenseLost,
      {},
    );
  }

  penalties.sort((a, b) => b.points - a.points || (a.code < b.code ? -1 : 1));

  const orderingBreaks = [...ordering.breaks].sort((a, b) => b.violations - a.violations);
  return {
    score,
    exactScore,
    version: AESTHETICS_VERSION,
    contract: input.contract ?? "inferred",
    bandSource: input.bands && input.bands.length > 0 ? (input.bandSource ?? null) : null,
    coreRangeSource: input.coreRange?.source ?? null,
    components,
    penalties,
    weights,
    blocking: {
      score: blockingBlend.score,
      detail: {
        shape: round3(shape.score),
        vertical: round3(vertical.sub.score),
        columns: round3(columns.sub.score),
        horizontal: round3(bands.sub.score),
        contiguity: round3(contiguity.sub.score),
      },
      shape: {
        score: shape.score,
        detail: Object.fromEntries(shape.levels.map((level) => [level.attr, level.score])),
      },
      vertical: vertical.sub,
      columns: columns.sub,
      horizontal: bands.sub,
      contiguity: contiguity.sub,
    },
    cdt: {
      score: cdtScore,
      // The detector key is absent, not zero, when the detector did not run.
      detail: {
        ...(detector ? { detector: round3(detector.score) } : {}),
        ordering: round3(ordering.sub.score),
      },
      detector,
      ordering: ordering.sub,
    },
    fullness: fullness.sub,
    ergonomics: shelfSense.sub,
    rulesKept: rulesKept.sub,
    range: range.sub,
    gates,
    gatesPassed,
    worst: {
      brokenBlocks: shape.worst.filter(isNamed),
      blocks: vertical.worst,
      columns: columns.worst,
      emptiestShelf: fullness.emptiest,
      airiestShelf: fullness.airiest,
      ordering: orderingBreaks[0] ?? null,
      rules: rulesKept.worst,
    },
    flagged: {
      brokenBlocks: shape.ranked,
      blocks: vertical.ragged,
      ordering: orderingBreaks,
      rules: rulesKept.breaks,
    },
    rules,
  };
};
